/**
 * AccountDeepSections — the drill-down + waste finder for a single account.
 *
 * Reads the deep subcollections from the Firestore store (search terms,
 * keywords, ads, ad groups, conversion actions) — the same data Andrei
 * reasons over. Tabbed, with the Search Terms tab as the headline: it
 * flags every term that spent money with zero conversions and totals the
 * waste, which is the single highest-ROI signal in account management.
 *
 * Drill-down: campaign + ad-group filter dropdowns narrow every tab, so you
 * can go account → campaign → ad group → its keywords / search terms / ads.
 *
 * Data is "as of last sync". A [sync this account] button re-pulls just this
 * account (read-only) when you want it fresher than the nightly run.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot, query as fsQuery } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../firebase';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { CF_BASE, AdsRange, callAdsQuery, microsToAmount, fullHistorySync, proposeChange, startGoogleAdsReconnect, looksLikeAdsAuthError } from './googleAdsApi';

interface MetricBucket {
  cost?: number; impressions?: number; clicks?: number;
  conversions?: number; conversionsValue?: number; ctr?: number; avgCpc?: number;
}

interface ImpressionShare { search?: number | null; budgetLost?: number | null; rankLost?: number | null; top?: number | null; absoluteTop?: number | null; exactMatch?: number | null; }
interface AdPolicy { approvalStatus?: string; reviewStatus?: string; topics?: Array<{ topic: string; type: string }>; }
interface QsComponents { adRelevance?: string | null; landingPage?: string | null; expectedCtr?: string | null; }

interface CampaignDoc { id: string; name?: string; status?: string; metrics30d?: MetricBucket; impressionShare?: ImpressionShare; }
interface AdGroupDoc { id: string; name?: string; status?: string; campaignId?: string; campaignName?: string; cpcBidMicros?: number; metrics30d?: MetricBucket; impressionShare?: ImpressionShare; }
interface AdDoc { id: string; type?: string; status?: string; adStrength?: string; headlines?: string[]; descriptions?: string[]; finalUrls?: string[]; adGroupId?: string; campaignId?: string; metrics30d?: MetricBucket; policy?: AdPolicy | null; }
interface KeywordDoc { criterionId?: string; text?: string; matchType?: string; status?: string; cpcBidMicros?: number; qualityScore?: number | null; adGroupId?: string; campaignId?: string; metrics30d?: MetricBucket; qsComponents?: QsComponents; }
interface SearchTermDoc {
  searchTerm?: string; status?: string; adGroupId?: string; campaignId?: string; metrics30d?: MetricBucket;
  /** Keyword(s) that triggered this term, cost-desc (search_term_view segmented
   *  by segments.keyword); a term can match more than one keyword over a range. */
  keywords?: Array<{ text: string; matchType?: string; cost: number }>;
  /** Landing page(s) — derived from the term's ad-group ad final URL, since
   *  search_term_view exposes no landing page of its own. Ad-group-level. */
  landingUrls?: string[];
}
interface ConversionActionDoc { id: string; name?: string; category?: string; type?: string; status?: string; countingType?: string; }
interface NegativeKeywordDoc { scope?: string; text?: string; matchType?: string; campaignId?: string; adGroupId?: string; criterionId?: string; campaignName?: string; adGroupName?: string; sharedSetName?: string; }
interface AssetDoc { assetId?: string; fieldType?: string; status?: string; approvalStatus?: string; text?: string; level?: string; campaignName?: string; adGroupName?: string; campaignId?: string; adGroupId?: string; finalUrl?: string; }
interface RecommendationDoc {
  resourceName?: string; type?: string; category?: string; risk?: string; dismissed?: boolean;
  campaignId?: string; adGroupId?: string;
  impact?: { deltaConversions?: number | null; deltaCost?: number | null; hasImpact?: boolean };
  detail?: { summary?: string } | null;
}
const REC_LABELS: Record<string, string> = {
  CAMPAIGN_BUDGET: 'Raise budget', FORECASTING_CAMPAIGN_BUDGET: 'Raise budget (forecast)', MOVE_UNUSED_BUDGET: 'Move unused budget',
  KEYWORD: 'Add keyword', KEYWORD_MATCH_TYPE: 'Change match type', USE_BROAD_MATCH_KEYWORD: 'Use broad match',
  TARGET_CPA_OPT_IN: 'Switch to Target CPA', TARGET_ROAS_OPT_IN: 'Switch to Target ROAS', MAXIMIZE_CONVERSIONS_OPT_IN: 'Maximize conversions',
  RESPONSIVE_SEARCH_AD: 'Add responsive search ad', RESPONSIVE_SEARCH_AD_IMPROVE_AD_STRENGTH: 'Improve ad strength', TEXT_AD: 'Add ad',
  SITELINK_ASSET: 'Add sitelinks', CALLOUT_ASSET: 'Add callouts', CALL_ASSET: 'Add call asset', LEAD_FORM_ASSET: 'Add lead form',
  SEARCH_PARTNERS_OPT_IN: 'Enable Search Partners', DISPLAY_EXPANSION_OPT_IN: 'Display expansion',
};
function recLabelF(type?: string): string {
  const t = String(type || '');
  if (REC_LABELS[t]) return REC_LABELS[t];
  const s = t.toLowerCase().replace(/_/g, ' ');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Recommendation';
}

const LIVE_WARN = '⚠ Applies immediately to the LIVE Google Ads account. Undo from “change activity” at the bottom of this page.';
// Common Google Ads languageConstant ids (mirrors CampaignWizard's COMMON_LANGUAGES;
// the campaign.addLanguage op accepts numeric ids only). F26.
const WIZARD_LANGUAGES: Array<{ id: string; name: string }> = [
  { id: '1000', name: 'English' }, { id: '1003', name: 'Spanish' }, { id: '1001', name: 'German' },
  { id: '1002', name: 'French' }, { id: '1004', name: 'Italian' }, { id: '1014', name: 'Portuguese' },
  { id: '1010', name: 'Dutch' }, { id: '1005', name: 'Japanese' }, { id: '1012', name: 'Korean' },
  { id: '1017', name: 'Chinese (simpl.)' }, { id: '1019', name: 'Arabic' }, { id: '1031', name: 'Russian' },
];
// Tier-2 segment rows (raw GoogleAdsRow shape from the range pulls).
interface SegRow { [k: string]: any; }

// AI Account Audit response (from the auditGoogleAdsAccount endpoint).
interface AuditPriority { severity: 'high' | 'medium' | 'low'; title: string; rationale: string; action: string; }
interface AuditResult {
  headline: string;
  healthScore: number;
  healthLabel: string;
  summary: string;
  priorities: AuditPriority[];
  quickWins: string[];
  watchOuts: string[];
}
interface AuditResponse {
  ok: boolean;
  name?: string;
  audit?: AuditResult;
  model?: { id?: string; provider?: string; used?: string };
  generatedAtMs?: number;
}

export interface AccountDeepSectionsProps {
  customerId: string;
  currency?: string;
  /** Selected timeframe — the deep tables (ad groups, keywords, search terms,
   *  ads) re-pull live for this window. */
  range?: AdsRange;
  /** Ancestor manager id to route the range pulls through (multi-MCC). */
  loginCustomerId?: string;
  /** Imperative focus request from an alert click — switch to `tab`, apply the
   *  given filter, and scroll into view. `nonce` re-triggers even if the target
   *  is unchanged (clicking the same alert twice). */
  focus?: { tab: Tab; wasteOnly?: boolean; adApproval?: 'disapproved'; nonce: number; cid?: string } | null;
  /** Mounted on an account with no activity in range (F24). The config tabs
   *  (negatives / extensions / conversions / recommendations) stay reachable so
   *  the user can set up account structure; the activity tabs default away and
   *  carry a hint that there's no data in this window. */
  noActivity?: boolean;
  /** Campaign IDs hidden on the campaigns table. Cascade the hide to their
   *  EXCLUSIVELY-OWNED children (ad groups / ads / keywords / search terms).
   *  Shared assets (extensions / shared lists / audiences) are deliberately NOT
   *  cascaded — they may belong to a still-visible campaign. */
  hiddenCampaigns?: Set<string>;
  /** When the campaigns table is revealing hidden campaigns, show their children
   *  here too (otherwise the cascade filters them out). */
  showHiddenCampaigns?: boolean;
}

/** Tabs that don't depend on range activity — always useful even on a fresh,
 *  no-spend account (config / setup surfaces). F24. */
const CONFIG_TABS: Tab[] = ['negatives', 'assets', 'conversions', 'recommendations'];

export type Tab = 'searchTerms' | 'keywords' | 'ads' | 'adGroups' | 'conversions'
  | 'negatives' | 'assets' | 'recommendations' | 'landingPages' | 'device' | 'geo' | 'hours' | 'demographics';

// ── Formatting ──────────────────────────────────────────────────────────
/** Google's REST API serializes proto int64 fields (clicks, impressions,
 *  costMicros, cpcBidMicros…) as JSON STRINGS; doubles arrive as numbers.
 *  Coerce BOTH at this boundary — a strict `typeof v === 'number'` check
 *  here once zeroed every clicks/impressions/bid cell in these tables while
 *  cost (already coerced by microsToAmount) survived, producing impossible
 *  rows like "$18.48 cost · 0 clicks". Mirrors GoogleAdsPage's n(). */
function n(v: string | number | undefined | null): number {
  if (v === undefined || v === null || v === '') return 0;
  const num = Number(v);
  return isFinite(num) ? num : 0;
}
function fmtCur(amount: number, currency?: string): string {
  if (!currency) return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount); }
  catch { return `${amount.toFixed(2)} ${currency}`; }
}
function fmtInt(amount: number): string { return new Intl.NumberFormat('en-AU').format(Math.round(amount)); }
function fmtPct(r: number): string { return isFinite(r) ? `${(r * 100).toFixed(2)}%` : '—'; }
function lower(s?: string): string { return String(s || '').toLowerCase().replace(/_/g, ' '); }
/** Path-only form of a URL — the host is the same for every row, so the PATH is
 *  the part that distinguishes two same-named sitelinks. Full URL on hover. */
function shortUrl(u?: string): string {
  const s = String(u || '');
  if (!s) return '';
  try { const p = new URL(s); return (p.pathname || '/') + (p.hash || ''); } catch { return s; }
}
/** Impression-share fraction → label. null → '—'; the 0.0999 sentinel → '<10%'. */
function isLabel(v: number | null | undefined): string { if (v === null || v === undefined) return '—'; if (v <= 0.0999) return '<10%'; return `${Math.round(v * 100)}%`; }
/** QS component bucket → short label. */
function qsShort(b?: string | null): string { const s = String(b || '').toUpperCase(); if (s === 'BELOW_AVERAGE') return 'below'; if (s === 'ABOVE_AVERAGE') return 'above'; if (s === 'AVERAGE') return 'avg'; return '—'; }
const isRemoved = (s?: string): boolean => String(s || '').toUpperCase() === 'REMOVED';
/** Inline "removed" badge — these entities are read-only in Google Ads; surfaced
 *  so a removed campaign/keyword/ad isn't mistaken for an actionable one. */
const RemovedTag: React.FC = () => <span style={{ color: '#c98a8a', fontSize: 10, marginLeft: 6, border: '1px solid #5a3a3a', borderRadius: 3, padding: '0 4px', verticalAlign: 'middle' }}>removed</span>;
const removedActionsNote = <span style={{ color: '#777' }} title="Removed entities are read-only in Google Ads">read-only</span>;

// All store rows carry the Firestore doc id (stable React key).
type WithDocId<T> = T & { docId: string };

// ── Generic store subscription hook ─────────────────────────────────────
function useSub<T>(customerId: string, sub: string): Array<WithDocId<T>> {
  const [rows, setRows] = useState<Array<WithDocId<T>>>([]);
  useEffect(() => {
    if (!customerId) { setRows([]); return; }
    const unsub = onSnapshot(
      fsQuery(collection(db, 'googleAdsAccounts', customerId, sub)),
      (snap) => setRows(snap.docs.map((d) => ({ docId: d.id, ...(d.data() as any) }))),
      (err) => { console.warn(`[AccountDeepSections] ${sub} sub error:`, err); setRows([]); },
    );
    return () => unsub();
  }, [customerId, sub]);
  return rows;
}

// ── On-demand range pull (deep entities aren't stored daily) ─────────────
// keywords / search terms / ads / ad groups are re-pulled live for the
// selected window via the date-parameterized named queries (1 op each).
interface RangePull<T> { rows: T[]; loading: boolean; error: string | null; }

function useRangePull<T>(
  customerId: string,
  loginCustomerId: string | undefined,
  queryName: string,
  range: AdsRange | undefined,
  mapFn: (row: any) => T,
  enabled = true,
  nonce = 0,           // bump to force a re-pull (e.g. after a write applies)
): RangePull<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const from = range?.from;
  const to = range?.to;
  useEffect(() => {
    if (!enabled || !customerId || !from || !to) { setRows([]); setError(null); return; }
    let cancelled = false;
    setLoading(true); setError(null);
    callAdsQuery<any>(customerId, queryName, loginCustomerId || undefined, { from, to })
      .then((resp) => {
        if (cancelled) return;
        if (resp.ok && resp.rows) setRows(resp.rows.map(mapFn));
        else { setRows([]); setError(resp.error || 'query failed'); }
      })
      .catch((e) => { if (!cancelled) { setRows([]); setError(e?.message || 'failed'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, loginCustomerId, queryName, from, to, mapFn, enabled, nonce]);
  return { rows, loading, error };
}

/** Stable identity mapper for raw-row range pulls (Tier-2 segments). */
const identityRow = (r: any): SegRow => r;
/** Raw metrics off a GoogleAdsRow. */
function segMetrics(r: any): { cost: number; impressions: number; clicks: number; conversions: number; conversionsValue: number } {
  const m = r?.metrics || {};
  return { cost: microsToAmount(m.costMicros), impressions: n(m.impressions), clicks: n(m.clicks), conversions: n(m.conversions), conversionsValue: n(m.conversionsValue) };
}

// Map raw GoogleAdsRow (camelCase REST shape) → the table doc shapes. Module-
// level so identities are stable (the hook deps on mapFn).
function bucketOf(m: any): MetricBucket {
  const cost = microsToAmount(m?.costMicros);
  const impressions = n(m?.impressions);
  const clicks = n(m?.clicks);
  return {
    cost, impressions, clicks,
    conversions: n(m?.conversions),
    conversionsValue: n(m?.conversionsValue),
    ctr: impressions > 0 ? clicks / impressions : 0,
    avgCpc: clicks > 0 ? cost / clicks : 0,
  };
}
const assetText = (arr: any): string[] => (Array.isArray(arr) ? arr.map((x: any) => x?.text).filter(Boolean) : []);
const isOf = (m: any): ImpressionShare => {
  const f = (v: any) => (v === undefined || v === null || v === '') ? null : Number(v);
  return { search: f(m?.searchImpressionShare), budgetLost: f(m?.searchBudgetLostImpressionShare), rankLost: f(m?.searchRankLostImpressionShare), top: f(m?.searchTopImpressionShare), absoluteTop: f(m?.searchAbsoluteTopImpressionShare) };
};

// Defensive both-shapes access (the sync reads snake_case OR camelCase from
// the searchStream JSON — mirror that here so a shape change can't blank the
// tables).
function mapAdGroupRow(row: any): WithDocId<AdGroupDoc> {
  const ag = row.ad_group || row.adGroup || {}; const c = row.campaign || {};
  return { docId: String(ag.id || ''), id: String(ag.id || ''), name: ag.name, status: ag.status, cpcBidMicros: n(ag.cpc_bid_micros ?? ag.cpcBidMicros), campaignId: c.id ? String(c.id) : '', campaignName: c.name, impressionShare: isOf(row.metrics), metrics30d: bucketOf(row.metrics) };
}
function mapAdRow(row: any): WithDocId<AdDoc> {
  const aga = row.ad_group_ad || row.adGroupAd || {}; const ad = aga.ad || {};
  const rsa = ad.responsive_search_ad || ad.responsiveSearchAd || {};
  const ag = row.ad_group || row.adGroup || {};
  const ps = aga.policy_summary || aga.policySummary;
  const policy: AdPolicy | null = ps ? { approvalStatus: String(ps.approval_status || ps.approvalStatus || ''), reviewStatus: String(ps.review_status || ps.reviewStatus || ''), topics: Array.isArray(ps.policy_topic_entries || ps.policyTopicEntries) ? (ps.policy_topic_entries || ps.policyTopicEntries).slice(0, 8).map((e: any) => ({ topic: String(e?.topic || ''), type: String(e?.type || '') })).filter((t: any) => t.topic) : [] } : null;
  return { docId: String(ad.id || ''), id: String(ad.id || ''), type: ad.type, status: aga.status, adStrength: aga.ad_strength || aga.adStrength, headlines: assetText(rsa.headlines), descriptions: assetText(rsa.descriptions), finalUrls: Array.isArray(ad.final_urls || ad.finalUrls) ? (ad.final_urls || ad.finalUrls) : [], policy, adGroupId: ag.id ? String(ag.id) : '', campaignId: row.campaign?.id ? String(row.campaign.id) : '', metrics30d: bucketOf(row.metrics) };
}
function mapKeywordRow(row: any): WithDocId<KeywordDoc> {
  const agc = row.ad_group_criterion || row.adGroupCriterion || {};
  const kw = agc.keyword || {};
  const qi = agc.quality_info || agc.qualityInfo || {};
  const ag = row.ad_group || row.adGroup || {};
  const critId = agc.criterion_id || agc.criterionId;
  return { docId: `${ag.id || ''}_${critId || ''}`, criterionId: String(critId || ''), status: agc.status || '', cpcBidMicros: n(agc.cpc_bid_micros ?? agc.cpcBidMicros), text: kw.text, matchType: kw.match_type || kw.matchType, qualityScore: (qi.quality_score ?? qi.qualityScore) ?? null, qsComponents: { adRelevance: qi.creative_quality_score ?? qi.creativeQualityScore ?? null, landingPage: qi.post_click_quality_score ?? qi.postClickQualityScore ?? null, expectedCtr: qi.search_predicted_ctr ?? qi.searchPredictedCtr ?? null }, adGroupId: ag.id ? String(ag.id) : '', campaignId: row.campaign?.id ? String(row.campaign.id) : '', metrics30d: bucketOf(row.metrics) };
}
function mapSearchTermRow(row: any): WithDocId<SearchTermDoc> {
  const stv = row.search_term_view || row.searchTermView || {};
  const ag = row.ad_group || row.adGroup || {};
  const term = stv.search_term || stv.searchTerm;
  return { docId: `${ag.id || ''}_${term || ''}`, searchTerm: term, status: stv.status, adGroupId: ag.id ? String(ag.id) : '', campaignId: row.campaign?.id ? String(row.campaign.id) : '', metrics30d: bucketOf(row.metrics) };
}
// One (search term × ad group × triggering keyword) attribution row, from the
// keyword-segmented search_terms_keywords_range query. Keyed by the SAME docId
// as a SearchTermDoc so the table can join keyword(s) onto each term. cost ranks
// the primary keyword when a term matched several.
interface SearchTermKeywordRow { docId: string; text: string; matchType: string; cost: number; }
function mapSearchTermKeywordRow(row: any): SearchTermKeywordRow {
  const stv = row.search_term_view || row.searchTermView || {};
  const ag = row.ad_group || row.adGroup || {};
  const term = stv.search_term || stv.searchTerm;
  const info = (row.segments?.keyword?.info || {}) as any;
  return {
    docId: `${ag.id || ''}_${term || ''}`,
    text: String(info.text || ''),
    matchType: String(info.match_type || info.matchType || ''),
    cost: microsToAmount(row.metrics?.costMicros ?? row.metrics?.cost_micros),
  };
}
// Compact second line under a search term: the triggering keyword(s) + a link
// to the (ad-group) landing page. Folded into the term cell so the table keeps
// its width — the data rides as a sub-line, not two extra columns.
const TermSubline: React.FC<{ t: SearchTermDoc }> = ({ t }) => {
  const kws = t.keywords || [];
  const urls = t.landingUrls || [];
  const primary = kws[0];
  const more = kws.length - 1;
  const u = urls[0];
  const kwList = kws.map((k) => `${k.text}${k.matchType ? ` (${lower(k.matchType)})` : ''}`).join(', ');
  return (
    <div style={{ fontSize: 11, color: '#6a6a6a', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {primary ? (
        <span title={`Triggered by ${kws.length === 1 ? 'keyword' : `${kws.length} keywords`}: ${kwList}`}>
          ▸ {primary.text}{primary.matchType ? ` · ${lower(primary.matchType)}` : ''}{more > 0 ? ` +${more}` : ''}
        </span>
      ) : (
        <span style={{ opacity: 0.65 }} title="No matched-keyword segment for this term (e.g. Dynamic Search Ads, or below Google’s reporting threshold).">▸ no keyword</span>
      )}
      {u && (
        <a href={u} target="_blank" rel="noopener noreferrer" className="gads-deep__url" title={urls.length > 1 ? `Landing pages (this ad group’s ad destinations):\n${urls.join('\n')}` : `Landing page — this ad group’s ad destination:\n${u}`}>
          ↗ {u.replace(/^https?:\/\//, '').replace(/\/+$/, '').slice(0, 40)}{urls.length > 1 ? ` +${urls.length - 1}` : ''}
        </a>
      )}
    </div>
  );
};

// One (campaign × ad-network) spend row — feeds the search-term reconciliation.
interface CampaignNetworkRow { campaignId: string; channelType: string; network: string; cost: number; clicks: number; }
function mapCampaignNetworkRow(row: any): CampaignNetworkRow {
  const c = row.campaign || {};
  const seg = row.segments || {};
  const m = row.metrics || {};
  return {
    campaignId: c.id ? String(c.id) : '',
    channelType: String(c.advertising_channel_type || c.advertisingChannelType || '').toUpperCase(),
    network: String(seg.ad_network_type || seg.adNetworkType || '').toUpperCase(),
    cost: microsToAmount(m.cost_micros ?? m.costMicros),
    clicks: n(m.clicks),
  };
}

// ─────────────────────────────────────────────────────────────────────────

/** Shared empty set so an absent `hiddenCampaigns` prop keeps a stable identity. */
const NO_HIDDEN_CAMPAIGNS: Set<string> = new Set();

export const AccountDeepSections: React.FC<AccountDeepSectionsProps> = ({ customerId, currency, range, loginCustomerId, focus, noActivity, hiddenCampaigns, showHiddenCampaigns }) => {
  // Stable reference when the prop is absent, so the memo/callback deps below
  // don't churn every render.
  const hidden = hiddenCampaigns ?? NO_HIDDEN_CAMPAIGNS;
  // Campaigns (filter dropdown) + conversion actions (config) stay on the
  // store snapshot; the metric tables re-pull live for the selected range.
  const campaigns = useSub<CampaignDoc>(customerId, 'campaigns');
  const conversions = useSub<ConversionActionDoc>(customerId, 'conversionActions');
  const negativeKeywords = useSub<NegativeKeywordDoc>(customerId, 'negativeKeywords');
  const assets = useSub<AssetDoc>(customerId, 'assets');
  const recommendations = useSub<RecommendationDoc>(customerId, 'recommendations');
  // Bumped after any write applies → forces the live deep-pull tables to re-fetch
  // so the change shows without a page reload. removedNegDocIds = optimistic
  // removals for the store-backed negatives tab (the synced store only reflects
  // a Google removal after the next sync).
  const [writeNonce, setWriteNonce] = useState(0);
  const [removedNegDocIds, setRemovedNegDocIds] = useState<Set<string>>(new Set());
  // Search terms negated THIS SESSION → docId → the info needed to remove that
  // negative again (criterionId parsed from the applied resourceName). Powers the
  // inline "undo" on a just-excluded search-term row.
  const [addedNegByTerm, setAddedNegByTerm] = useState<Map<string, { criterionId: string; campaignId: string; scope: 'campaign' | 'adGroup'; text: string; matchType: string }>>(new Map());
  const [excludedNoUndo, setExcludedNoUndo] = useState<Set<string>>(new Set()); // negated this session but no resourceName came back → optimistic-excluded, no inline undo
  const [undoingTerm, setUndoingTerm] = useState('');   // docId currently being undone
  const adGroupsPull = useRangePull<WithDocId<AdGroupDoc>>(customerId, loginCustomerId, 'ad_groups_range', range, mapAdGroupRow, true, writeNonce);
  const adsPull = useRangePull<WithDocId<AdDoc>>(customerId, loginCustomerId, 'ads_range', range, mapAdRow, true, writeNonce);
  const keywordsPull = useRangePull<WithDocId<KeywordDoc>>(customerId, loginCustomerId, 'keywords_range', range, mapKeywordRow, true, writeNonce);
  const searchTermsPull = useRangePull<WithDocId<SearchTermDoc>>(customerId, loginCustomerId, 'search_terms_range', range, mapSearchTermRow, true, writeNonce);
  const adGroups = adGroupsPull.rows;
  const ads = adsPull.rows;
  const keywords = keywordsPull.rows;
  // Ad group → its ads' distinct final URLs. search_term_view has no landing
  // page, so the search-terms table surfaces the destination the term's ad
  // group sends clicks to (accurate when an ad group has one final URL — the
  // common case; clearly an ad-group-level signal when there are several).
  // Prefer ENABLED ads (what currently serves); fall back to all of the ad
  // group's ads only if none are enabled, so an all-paused group still resolves.
  const adGroupUrls = useMemo(() => {
    const collect = (pred: (ad: WithDocId<AdDoc>) => boolean) => {
      const m = new Map<string, string[]>();
      for (const ad of ads) {
        if (!pred(ad)) continue;
        const agId = String(ad.adGroupId || '');
        if (!agId || !ad.finalUrls || ad.finalUrls.length === 0) continue;
        const set = m.get(agId) || [];
        for (const raw of ad.finalUrls) { const s = String(raw || ''); if (s && !set.includes(s)) set.push(s); }
        m.set(agId, set);
      }
      return m;
    };
    const enabled = collect((ad) => /enabled/i.test(String(ad.status || '')));
    const all = collect(() => true);
    // Merge: enabled wins; fall back to all for groups with no enabled-ad URL.
    all.forEach((urls, agId) => { if (!enabled.has(agId)) enabled.set(agId, urls); });
    return enabled;
  }, [ads]);
  const rangeLabel = range?.label || 'last 30 days';

  // On a no-activity account (F24) open to a config tab — the activity tabs have
  // nothing to show in this window, but the setup tabs are exactly what's wanted.
  // Also honor a deep-link focus that targets THIS account at mount (e.g. the
  // portfolio "N recs" badge → recommendations). cid-scoped so a stale focus from
  // a previously-viewed account can't hijack this one's initial tab — the
  // focus-nonce ref below deliberately ignores a focus already set at mount, which
  // is exactly the navigate-from-portfolio case, so we seed the initial tab here.
  const [tab, setTab] = useState<Tab>(() =>
    (focus && focus.cid === customerId && focus.tab)
      ? focus.tab
      : (noActivity ? 'recommendations' : 'searchTerms'));
  // Per-(campaign, network) spend — only when the search-terms tab is open —
  // so the reconciliation can attribute spend that search_term_view can't
  // itemise (Display / PMax / Search-Partners / YouTube). Lazy: 1 op per open.
  const campNetPull = useRangePull<CampaignNetworkRow>(customerId, loginCustomerId, 'campaign_network_range', range, mapCampaignNetworkRow, tab === 'searchTerms', writeNonce);
  // Triggering-keyword attribution — a SEPARATE label-only pull (see the backend
  // note on search_terms_keywords_range). Kept off the base search-terms query so
  // that query stays complete + exact; this only enriches each term with the
  // keyword(s) that fired it. Lazy: only when the search-terms tab is open.
  const keywordsByTermPull = useRangePull<SearchTermKeywordRow>(customerId, loginCustomerId, 'search_terms_keywords_range', range, mapSearchTermKeywordRow, tab === 'searchTerms', writeNonce);
  const kwByTerm = useMemo(() => {
    const m = new Map<string, Array<{ text: string; matchType: string; cost: number }>>();
    for (const r of keywordsByTermPull.rows) {
      if (!r.docId || !r.text) continue;
      const arr = m.get(r.docId);
      if (arr) arr.push({ text: r.text, matchType: r.matchType, cost: r.cost });
      else m.set(r.docId, [{ text: r.text, matchType: r.matchType, cost: r.cost }]);
    }
    // Rank each term's keywords by spend so the table shows the primary first.
    m.forEach((arr) => arr.sort((a, b) => b.cost - a.cost));
    return m;
  }, [keywordsByTermPull.rows]);
  // The base pull is one exact row per (term, ad group); enrich each with its
  // triggering keyword(s) and the derived ad-group landing URL(s). No metric
  // aggregation — the base query isn't keyword-split, so cost/clicks are exact.
  const searchTerms = useMemo<Array<WithDocId<SearchTermDoc>>>(() =>
    searchTermsPull.rows.map((r) => ({
      ...r,
      keywords: kwByTerm.get(String((r as any).docId || '')) || [],
      landingUrls: adGroupUrls.get(String(r.adGroupId || '')) || [],
    })),
  [searchTermsPull.rows, kwByTerm, adGroupUrls]);
  const [campFilter, setCampFilter] = useState('');
  const [agFilter, setAgFilter] = useState('');
  /** Ads tab: narrow to disapproved / limited ads (set by a 'disapproved ad'
   *  alert click, or the toggle in the ads subhead). */
  const [adApproval, setAdApproval] = useState<'all' | 'disapproved'>('all');
  /** Root element — alert clicks scroll the deep sections into view. */
  const rootRef = useRef<HTMLElement | null>(null);
  /** Free-text filter applied to the ACTIVE tab's primary text field
   *  (term / keyword / headline / name / url …). Lives in the deep bar. */
  const [textQuery, setTextQuery] = useState('');
  /** Search-terms-tab specific: status + waste-only narrowing. Status uses
   *  the DERIVED value (raw status OR negated this session). */
  const [termStatus, setTermStatus] = useState<'all' | 'active' | 'excluded'>('all');
  const [wasteOnly, setWasteOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fullSyncing, setFullSyncing] = useState(false);
  const [fullSyncMsg, setFullSyncMsg] = useState('');
  const [actionModal, setActionModal] = useState<{ title: string; lines: React.ReactNode; warn?: React.ReactNode; danger?: boolean; ops: any[]; kind?: string; rationale?: string; input?: 'bid' | 'keyword' | 'pct' | 'adgroup' | 'sitelink' | 'callout' | 'sharedListName' | 'sharedListNegative' | 'attachCampaign' | 'biddingStrategy' | 'networks' | 'deviceBid' | 'adSchedule' | 'location' | 'language'; draftOnly?: boolean; onApplied?: (resp?: { ok: boolean; changeSet?: any; applied?: any }) => void } | null>(null);
  const [actionApplying, setActionApplying] = useState(false);
  const [modalBid, setModalBid] = useState('');       // bid input (currency)
  const [modalKwText, setModalKwText] = useState('');  // add-keyword text
  const [modalKwMatch, setModalKwMatch] = useState('PHRASE');
  const [modalPct, setModalPct] = useState('');        // device bid-adjustment percent
  // ── F48: range-pull error banner (retry + conditional reconnect) ──
  const [rangePullError, setRangePullError] = useState<{ msg: string; tab?: Tab; nonce?: number } | null>(null);
  const [suggestReconnect, setSuggestReconnect] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  // ── F27: extension (sitelink / callout) add-form state ──
  const [assetText, setAssetText] = useState('');
  const [assetUrl, setAssetUrl] = useState('');
  const [assetDesc1, setAssetDesc1] = useState('');
  const [assetDesc2, setAssetDesc2] = useState('');
  const [assetScope, setAssetScope] = useState('campaign');
  // ── F47: shared negative-list add-form state ──
  const [sharedListName, setSharedListName] = useState('');
  const [sharedSetIdInput, setSharedSetIdInput] = useState('');
  const [sharedNegText, setSharedNegText] = useState('');
  const [sharedNegMatch, setSharedNegMatch] = useState('EXACT');
  const [attachCampaignId, setAttachCampaignId] = useState('');
  // ── F26: existing-campaign edit form state ──
  const [editCampaign, setEditCampaign] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [editBiddingStrategy, setEditBiddingStrategy] = useState('MANUAL_CPC');
  const [editTargetCpa, setEditTargetCpa] = useState('');
  const [editTargetRoas, setEditTargetRoas] = useState('');
  const [editSearchPartners, setEditSearchPartners] = useState(false);
  const [editContentNetwork, setEditContentNetwork] = useState(false);
  const [editDeviceType, setEditDeviceType] = useState('MOBILE');
  const [editDeviceModifier, setEditDeviceModifier] = useState('');   // percent input
  const [editScheduleDay, setEditScheduleDay] = useState('MONDAY');
  const [editScheduleStart, setEditScheduleStart] = useState('8');
  const [editScheduleEnd, setEditScheduleEnd] = useState('18');
  const [editScheduleModifier, setEditScheduleModifier] = useState('');   // percent input
  const [editGeoId, setEditGeoId] = useState('');
  const [editLanguageId, setEditLanguageId] = useState('1000');
  // RSA authoring panel (create responsive search ad) — the first authoring
  // surface: too much content for the confirm modal, so it gets its own panel.
  const [rsaOpen, setRsaOpen] = useState(false);
  // Target snapshotted at open — the panel must not retarget if the underlying
  // ad-group filter changes (or resets) while it's open.
  const [rsaTarget, setRsaTarget] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [rsaUrl, setRsaUrl] = useState('');
  const [rsaPath1, setRsaPath1] = useState('');
  const [rsaPath2, setRsaPath2] = useState('');
  const [rsaHeads, setRsaHeads] = useState<Array<{ text: string; pin: string }>>([]);
  const [rsaDescs, setRsaDescs] = useState<Array<{ text: string; pin: string }>>([]);
  const [rsaApplying, setRsaApplying] = useState(false);

  // Tier-2 segment pulls — lazy (only fetch when their tab is active) so they
  // don't spend ops on tabs the user never opens.
  const landingPull = useRangePull<SegRow>(customerId, loginCustomerId, 'landing_pages_range', range, identityRow, tab === 'landingPages', writeNonce);
  const devicePull = useRangePull<SegRow>(customerId, loginCustomerId, 'device_range', range, identityRow, tab === 'device', writeNonce);
  const hoursPull = useRangePull<SegRow>(customerId, loginCustomerId, 'hour_of_week_range', range, identityRow, tab === 'hours', writeNonce);
  const agePull = useRangePull<SegRow>(customerId, loginCustomerId, 'age_range_perf', range, identityRow, tab === 'demographics', writeNonce);
  const genderPull = useRangePull<SegRow>(customerId, loginCustomerId, 'gender_range_perf', range, identityRow, tab === 'demographics', writeNonce);
  const geoPull = useRangePull<SegRow>(customerId, loginCustomerId, 'geo_range', range, identityRow, tab === 'geo', writeNonce);
  const geoNamesPull = useRangePull<SegRow>(customerId, loginCustomerId, 'geo_target_names', range, identityRow, tab === 'geo', writeNonce);
  // F25: per-conversion-action performance for the selected window (conversion
  // metrics only — Google restricts segments.conversion_action to that family).
  const conversionsPerfPull = useRangePull<SegRow>(customerId, loginCustomerId, 'conversion_actions_perf_range', range, identityRow, tab === 'conversions', writeNonce);

  // AI Account Audit state.
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState<AuditResponse | null>(null);
  const [auditError, setAuditError] = useState('');

  // Reset filters + clear any prior audit when the account changes.
  useEffect(() => { setCampFilter(''); setAgFilter(''); setTextQuery(''); setTermStatus('all'); setWasteOnly(false); setAdApproval('all'); setAudit(null); setAuditError(''); }, [customerId]);
  // The text query is per-tab in spirit — clear it when the tab changes so
  // a leftover keyword filter can't silently blank the next tab.
  useEffect(() => { setTextQuery(''); }, [tab]);

  // F48: surface the FIRST range-pull error into a dismissible banner with a
  // retry + a conditional reconnect hint (matching the GoogleAdsPage UX). The
  // deep tables already swallow their error into the empty cell; this lifts it
  // to an actionable banner above the tabs without disturbing the tables.
  useEffect(() => {
    const firstErr = [adGroupsPull, adsPull, keywordsPull, searchTermsPull,
      landingPull, devicePull, hoursPull, agePull, genderPull, geoPull,
      conversionsPerfPull].find((p) => p.error);
    if (firstErr?.error) {
      setSuggestReconnect(looksLikeAdsAuthError(firstErr.error));
      setRangePullError({ msg: firstErr.error, tab, nonce: writeNonce });
    } else {
      setRangePullError(null);
      setSuggestReconnect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupsPull.error, adsPull.error, keywordsPull.error, searchTermsPull.error,
    landingPull.error, devicePull.error, hoursPull.error, agePull.error,
    genderPull.error, geoPull.error, conversionsPerfPull.error, tab, writeNonce]);

  // Alert-driven focus: switch to the requested tab, apply its filter, and
  // scroll into view. Keyed on nonce so the same alert can re-focus.
  //
  // Initialised to the CURRENT focus nonce so the effect is a no-op on mount —
  // otherwise a stale deepFocus left over from a previous account (the parent
  // never clears it, and this component remounts when selectedCid toggles) would
  // hijack the new account's tab/filter on first render. Only a nonce that
  // ADVANCES after mount (i.e. a real new click) applies.
  const appliedFocusNonce = useRef<number | undefined>(focus?.nonce);
  useEffect(() => {
    if (!focus) return;
    if (focus.cid && focus.cid !== customerId) return;   // a focus meant for another account
    if (appliedFocusNonce.current === focus.nonce) return;
    appliedFocusNonce.current = focus.nonce;
    setTab(focus.tab);
    if (focus.tab === 'searchTerms') { setWasteOnly(!!focus.wasteOnly); setTermStatus('all'); }
    if (focus.tab === 'ads') setAdApproval(focus.adApproval === 'disapproved' ? 'disapproved' : 'all');
    // Defer the scroll until after the tab content has rendered.
    const t = setTimeout(() => { rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce]);

  // Ad-group dropdown options narrow to the selected campaign.
  const agOptions = useMemo(
    () => adGroups.filter((ag) => !campFilter || ag.campaignId === campFilter),
    [adGroups, campFilter],
  );

  // Guard against a stale ad-group filter: if the selected ad group is no
  // longer in the option list (campaign changed, or data shifted), the
  // <select> falls back to showing "all ad groups" while agFilter still
  // holds the old id — which would silently filter every row out. Clear it.
  useEffect(() => {
    if (agFilter && !agOptions.some((a) => a.id === agFilter)) setAgFilter('');
  }, [agOptions, agFilter]);

  // Generic filter predicate for rows carrying campaignId / adGroupId.
  const matches = useCallback((r: { campaignId?: string; adGroupId?: string }) => {
    // Cascade the campaigns-table hide to a campaign's exclusively-owned
    // children (keywords / ads / search terms). Suppressed when the campaigns
    // table is revealing hidden rows.
    if (!showHiddenCampaigns && r.campaignId && hidden.has(r.campaignId)) return false;
    if (campFilter && r.campaignId !== campFilter) return false;
    if (agFilter && r.adGroupId !== agFilter) return false;
    return true;
  }, [campFilter, agFilter, hidden, showHiddenCampaigns]);

  // Derived search-term exclusion status — the raw API status OR a negative
  // added this session (optimistic). The status FILTER and the status SORT
  // must both use this, or just-negated rows would filter/sort wrong.
  const termBlocked = useCallback((t: { status?: string } & { docId?: string }) => {
    const docId = String((t as any).docId || '');
    return /exclud/i.test(String(t.status || '')) || excludedNoUndo.has(docId) || addedNegByTerm.has(docId);
  }, [excludedNoUndo, addedNegByTerm]);

  // ── Search-term waste finder ──────────────────────────────────────
  const filteredTerms = useMemo(() => {
    const q = textQuery.trim().toLowerCase();
    return searchTerms
      .filter(matches)
      .filter((t) => !q || String(t.searchTerm || '').toLowerCase().includes(q))
      .filter((t) => {
        if (termStatus === 'all') return true;
        const blocked = termBlocked(t);
        return termStatus === 'excluded' ? blocked : !blocked;
      })
      .filter((t) => !wasteOnly || (n(t.metrics30d?.cost) > 0 && n(t.metrics30d?.conversions) === 0))
      .sort((a, b) => n(b.metrics30d?.cost) - n(a.metrics30d?.cost));
  }, [searchTerms, matches, textQuery, termStatus, wasteOnly, termBlocked]);
  const waste = useMemo(() => {
    // ACTIONABLE waste = zero-conversion spend on terms NOT yet excluded — the
    // only thing the user can still act on. Already-excluded terms keep their
    // historical zero-conv spend (it predates the negative), so counting them
    // as live "waste" overstates the opportunity and contradicts the per-row
    // "excluded" affordance. Split them out and report each honestly.
    let cost = 0; let count = 0;
    let blockedCost = 0; let blockedCount = 0;
    for (const t of filteredTerms) {
      const c = n(t.metrics30d?.cost);
      if (c > 0 && n(t.metrics30d?.conversions) === 0) {
        if (termBlocked(t)) { blockedCost += c; blockedCount += 1; }
        else { cost += c; count += 1; }
      }
    }
    return { cost, count, blockedCost, blockedCount };
  }, [filteredTerms, termBlocked]);

  // ── Search-term ↔ campaign-spend reconciliation ───────────────────
  // search_term_view only itemises GOOGLE SEARCH queries above Google's
  // reporting threshold. So a campaign's spend on the Display/Content network
  // (search campaigns with display expansion), Search Partners, YouTube, or a
  // Performance Max campaign produces NO search-term rows — the terms live in
  // other reports or aren't itemised at all. This attributes the gap by source
  // so the totals are explainable rather than looking like dropped data.
  // Scoped to the campaign filter; not shown when an ad-group filter is active
  // (campaign-network spend can't be split by ad group).
  const reconcile = useMemo(() => {
    const rows = campNetPull.rows.filter((r) => !campFilter || r.campaignId === campFilter);
    if (rows.length === 0) return null;
    // Track BOTH clicks and cost per source — the user reads the clicks column,
    // so clicks must reconcile as visibly as spend.
    const z = () => ({ cost: 0, clicks: 0 });
    const total = z(); const pmax = z(); const display = z(); const youtube = z(); const other = z();
    const add = (b: { cost: number; clicks: number }, r: CampaignNetworkRow) => { b.cost += r.cost; b.clicks += r.clicks; };
    for (const r of rows) {
      add(total, r);
      if (r.channelType === 'PERFORMANCE_MAX') { add(pmax, r); continue; }
      switch (r.network) {
        case 'CONTENT': add(display, r); break;
        case 'YOUTUBE_SEARCH': case 'YOUTUBE_WATCH': add(youtube, r); break;
        case 'SEARCH': case 'SEARCH_PARTNERS': break; // search-term-bearing networks
        default: add(other, r); break;
      }
    }
    const terms = searchTerms.filter((t) => !campFilter || t.campaignId === campFilter);
    const itemized = {
      cost: terms.reduce((s, t) => s + n(t.metrics30d?.cost), 0),
      clicks: terms.reduce((s, t) => s + n(t.metrics30d?.clicks), 0),
    };
    const unattributed = { cost: Math.max(0, total.cost - itemized.cost), clicks: Math.max(0, total.clicks - itemized.clicks) };
    // Networks that structurally cannot produce search terms.
    const nonSearch = { cost: pmax.cost + display.cost + youtube.cost + other.cost, clicks: pmax.clicks + display.clicks + youtube.clicks + other.clicks };
    // Remainder = Google Search / Search Partners spend on queries Google
    // withholds as low-volume (the genuine privacy threshold).
    const lowVolume = { cost: Math.max(0, unattributed.cost - nonSearch.cost), clicks: Math.max(0, unattributed.clicks - nonSearch.clicks) };
    return { total, itemized, unattributed, pmax, display, youtube, other, lowVolume };
  }, [campNetPull.rows, campFilter, searchTerms]);

  const filteredKeywords = useMemo(() => {
    const q = textQuery.trim().toLowerCase();
    return keywords
      .filter(matches)
      .filter((k) => !q || String(k.text || '').toLowerCase().includes(q))
      .sort((a, b) => n(b.metrics30d?.cost) - n(a.metrics30d?.cost));
  }, [keywords, matches, textQuery]);
  const filteredAds = useMemo(() => {
    const q = textQuery.trim().toLowerCase();
    const isBadPolicy = (a: AdDoc) => ['DISAPPROVED', 'APPROVED_LIMITED', 'AREA_OF_INTEREST_ONLY'].includes(String(a.policy?.approvalStatus || '').toUpperCase());
    return ads
      .filter(matches)
      .filter((a) => adApproval === 'all' || isBadPolicy(a))
      .filter((a) => !q
        || (a.headlines || []).some((h) => String(h).toLowerCase().includes(q))
        || (a.finalUrls || []).some((u) => String(u).toLowerCase().includes(q)))
      .sort((a, b) => n(b.metrics30d?.cost) - n(a.metrics30d?.cost));
  }, [ads, matches, textQuery, adApproval]);
  const filteredAdGroups = useMemo(() => {
    const q = textQuery.trim().toLowerCase();
    return adGroups
      // Cascade the campaign hide to ad groups (exclusively campaign-owned).
      .filter((ag) => showHiddenCampaigns || !ag.campaignId || !hidden.has(ag.campaignId))
      .filter((ag) => !campFilter || ag.campaignId === campFilter)
      .filter((ag) => !q || String(ag.name || '').toLowerCase().includes(q))
      .sort((a, b) => n(b.metrics30d?.cost) - n(a.metrics30d?.cost));
  }, [adGroups, campFilter, textQuery, hidden, showHiddenCampaigns]);

  const syncThis = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await authFetch(`${CF_BASE}/syncGoogleAdsAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      toast.success('Account re-synced.');
    } catch (e: any) {
      toast.error(`Sync failed: ${e?.message || 'unknown error'}`);
    } finally {
      setSyncing(false);
    }
  }, [customerId]);

  // ── Full-history sync (per account) ───────────────────────────────
  // Pulls the COMPLETE daily time-series (back to the retention floor) into the
  // store so every timeframe is instant. The endpoint may pause at its soft
  // deadline; re-invoke until it reports done. The default limited sync above
  // is unaffected. Read-only against Google Ads.
  const fullSyncThis = useCallback(async () => {
    setFullSyncing(true);
    setFullSyncMsg('starting…');
    try {
      let done = false;
      for (let i = 0; i < 12 && !done; i += 1) {
        const r = await fullHistorySync(customerId, loginCustomerId);
        if (!r.ok) throw new Error(r.error || 'full sync failed');
        if (r.windowsTotal) setFullSyncMsg(`${r.windowsDone}/${r.windowsTotal} windows · ${(r.dayCellsWritten || 0).toLocaleString()} days`);
        done = r.state === 'done';
        if (r.state === 'paused') setFullSyncMsg(`resuming… (${r.windowsDone}/${r.windowsTotal})`);
      }
      if (done) toast.success('Full history synced.');
      else toast('Full sync still running — re-run to continue.');
    } catch (e: any) {
      toast.error(`Full sync failed: ${e?.message || 'unknown error'}`);
    } finally {
      setFullSyncing(false);
      setFullSyncMsg('');
    }
  }, [customerId, loginCustomerId]);

  // ── AI Account Audit ──────────────────────────────────────────────
  // Runs the same rule engine the CLI uses, then has the LLM synthesise a
  // prioritised, plain-English audit FOR THE SELECTED WINDOW. Read-only.
  const runAudit = useCallback(async () => {
    setAuditing(true);
    setAuditError('');
    try {
      const r = await authFetch(`${CF_BASE}/auditGoogleAdsAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(range ? { customerId, from: range.from, to: range.to } : { customerId }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      setAudit(data as AuditResponse);
    } catch (e: any) {
      setAuditError(e?.message || 'Audit failed');
      toast.error(`Audit failed: ${e?.message || 'unknown error'}`);
    } finally {
      setAuditing(false);
    }
  }, [customerId, range]);

  // Geo criterion-id → readable name (resolved lazily when the geo tab opens).
  const geoNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const g of geoNamesPull.rows) {
      const gc = (g.geoTargetConstant || g.geo_target_constant || {}) as any;
      if (gc.id) map[String(gc.id)] = gc.canonicalName || gc.canonical_name || gc.name || String(gc.id);
    }
    return map;
  }, [geoNamesPull.rows]);
  const geoName = useCallback((resourceName?: string): string => {
    if (!resourceName) return '';
    const id = String(resourceName).split('/').pop() || '';
    return geoNameMap[id] || id;
  }, [geoNameMap]);

  /** Lowercased text query for the small tabs that filter inline (the
   *  big entity tabs filter inside their memos). */
  const tq = textQuery.trim().toLowerCase();

  const tabs: Array<{ key: Tab; label: string; count?: number }> = [
    { key: 'searchTerms', label: 'search terms', count: searchTerms.length },
    { key: 'keywords', label: 'keywords', count: keywords.length },
    { key: 'ads', label: 'ads', count: ads.length },
    { key: 'adGroups', label: 'ad groups', count: adGroups.length },
    { key: 'negatives', label: 'negatives', count: negativeKeywords.length },
    { key: 'assets', label: 'extensions', count: assets.length },
    { key: 'recommendations', label: 'recommendations', count: recommendations.filter((r) => !r.dismissed).length },
    { key: 'conversions', label: 'conversions', count: conversions.length },
    { key: 'landingPages', label: 'landing pages' },
    { key: 'device', label: 'device' },
    { key: 'geo', label: 'geo' },
    { key: 'hours', label: 'hours' },
    { key: 'demographics', label: 'demographics' },
  ];

  const nameOfCampaign = useCallback(
    (id?: string) => campaigns.find((c) => c.id === id)?.name || '',
    [campaigns],
  );
  const nameOfAdGroup = useCallback(
    (id?: string) => adGroups.find((a) => a.id === id)?.name || '',
    [adGroups],
  );

  // Recommendations: safe quick-wins first, then by estimated extra conversions.
  // Dismissed recs are excluded entirely — once dismissed they should be gone,
  // not lingering as dead "(dismissed)" rows. A genuinely-still-live rec that was
  // only marked dismissed locally re-appears on the next sync (Google's query
  // returns non-dismissed recs), so this self-corrects.
  const recsSorted = useMemo(() => {
    const order: Record<string, number> = { safe: 0, review: 1, high: 2 };
    return recommendations.filter((r) => !r.dismissed).sort((a, b) => {
      const ra = order[a.risk || 'review'] ?? 1; const rb = order[b.risk || 'review'] ?? 1;
      if (ra !== rb) return ra - rb;
      return n(b.impact?.deltaConversions) - n(a.impact?.deltaConversions);
    });
  }, [recommendations]);

  // F25: merge config conversion actions (store) with per-action performance
  // (range pull, keyed by conversion_action_name). Perf rows carry only the
  // conversion family of metrics; matched into the config row by name. Config
  // actions with no perf in the window show zeros; perf rows with no config
  // match still appear (so a renamed/removed action's data isn't dropped).
  type ConvRow = ConversionActionDoc & { conversions: number; allConversions: number; conversionsValue: number; allConversionsValue: number; hasConfig: boolean };
  const conversionRows = useMemo<ConvRow[]>(() => {
    const perfByName = new Map<string, { conversions: number; allConversions: number; conversionsValue: number; allConversionsValue: number }>();
    for (const r of conversionsPerfPull.rows) {
      const seg = (r.segments || {}) as any;
      const name = String(seg.conversionActionName || seg.conversion_action_name || '');
      if (!name) continue;
      const m = (r.metrics || {}) as any;
      const prev = perfByName.get(name) || { conversions: 0, allConversions: 0, conversionsValue: 0, allConversionsValue: 0 };
      perfByName.set(name, {
        conversions: prev.conversions + n(m.conversions),
        allConversions: prev.allConversions + n(m.allConversions ?? m.all_conversions),
        conversionsValue: prev.conversionsValue + n(m.conversionsValue ?? m.conversions_value),
        allConversionsValue: prev.allConversionsValue + n(m.allConversionsValue ?? m.all_conversions_value),
      });
    }
    const seen = new Set<string>();
    const out: ConvRow[] = [];
    for (const c of conversions) {
      const name = String(c.name || '');
      const p = perfByName.get(name) || { conversions: 0, allConversions: 0, conversionsValue: 0, allConversionsValue: 0 };
      seen.add(name);
      out.push({ ...c, ...p, hasConfig: true });
    }
    // Perf-only rows (no matching config doc). forEach avoids direct Map
    // iteration (the build target lacks downlevelIteration).
    perfByName.forEach((p, name) => {
      if (seen.has(name)) return;
      out.push({ id: name, name, ...p, hasConfig: false });
    });
    return out;
  }, [conversions, conversionsPerfPull.rows]);

  // F48: retry a failed range pull by re-bumping the write nonce (the same
  // mechanism used to re-pull after a write applies), and clear the banner.
  const handleRangeRetry = useCallback(() => {
    setWriteNonce((x) => x + 1);
    setRangePullError(null);
  }, []);
  // F48: inline OAuth reconnect — runs the consent popup, then re-pulls. Mirrors
  // the GoogleAdsPage reconnect flow so the deep tables recover without leaving
  // the page.
  const handleReconnect = useCallback(async () => {
    if (reconnecting) return;
    setReconnecting(true);
    try {
      const r = await startGoogleAdsReconnect();
      if (!r.ok) { toast.error(`Reconnect failed: ${r.error || 'unknown'}`); return; }
      toast.success('Reconnected — retrying…');
      setSuggestReconnect(false);
      setRangePullError(null);
      setWriteNonce((x) => x + 1);
    } finally {
      setReconnecting(false);
    }
  }, [reconnecting]);

  // The confirm modal IS the approval for user-initiated changes — on confirm it
  // applies DIRECTLY to the live account (bypassing the Andrei review queue +
  // kill switch). Still gated server-side by the holy disabled-account rule +
  // Google dry-run validation. Each opener below pops the modal with typed ops.
  const confirmAction = useCallback(async () => {
    if (!actionModal) return;
    let ops = actionModal.ops;
    if (actionModal.input === 'bid') {
      const micros = Math.round(parseFloat(modalBid) * 1e6);
      if (!isFinite(micros) || micros <= 0) { toast.error('Enter a valid max CPC.'); return; }
      ops = ops.map((o) => ({ ...o, cpcBidMicros: micros }));
    } else if (actionModal.input === 'keyword') {
      const t = modalKwText.trim();
      if (!t) { toast.error('Enter a keyword.'); return; }
      ops = ops.map((o) => ({ ...o, text: t, matchType: modalKwMatch }));
    } else if (actionModal.input === 'pct') {
      const pct = parseFloat(modalPct);
      if (!isFinite(pct) || pct < -100 || pct > 900) { toast.error('Enter a % between -100 and 900.'); return; }
      const mult = Math.round((1 + pct / 100) * 100) / 100;
      ops = ops.map((o) => ({ ...o, bidModifier: mult }));
    } else if (actionModal.input === 'adgroup') {
      const nm = modalKwText.trim();
      if (!nm) { toast.error('Enter an ad group name.'); return; }
      const bid = parseFloat(modalBid);
      const bidMicros = isFinite(bid) && bid > 0 ? Math.round(bid * 1e6) : undefined;
      ops = ops.map((o) => ({ ...o, name: nm, ...(bidMicros ? { cpcBidMicros: bidMicros } : {}) }));
    } else if (actionModal.input === 'sitelink') {       // F27
      const t = assetText.trim(); const u = assetUrl.trim();
      const d1 = assetDesc1.trim(); const d2 = assetDesc2.trim();
      if (!t) { toast.error('Enter sitelink text.'); return; }
      if (t.length > 25) { toast.error('Sitelink text max 25 chars.'); return; }
      if (!u || !/^https?:\/\/.+\..+/.test(u)) { toast.error('Enter valid http(s) url.'); return; }
      if ((d1 && !d2) || (d2 && !d1)) { toast.error('Provide both description lines or neither.'); return; }
      if (d1.length > 35 || d2.length > 35) { toast.error('Descriptions max 35 chars each.'); return; }
      ops = ops.map((o) => ({ ...o, text: t, url: u, ...(d1 ? { desc1: d1, desc2: d2 } : {}) }));
    } else if (actionModal.input === 'callout') {        // F27
      const t = assetText.trim();
      if (!t) { toast.error('Enter callout text.'); return; }
      if (t.length > 25) { toast.error('Callout text max 25 chars.'); return; }
      ops = ops.map((o) => ({ ...o, text: t, scopeLevel: assetScope }));
    } else if (actionModal.input === 'sharedListName') { // F47
      const nm = sharedListName.trim();
      if (!nm) { toast.error('Enter a list name.'); return; }
      if (nm.length > 255) { toast.error('List name max 255 chars.'); return; }
      ops = ops.map((o) => ({ ...o, listName: nm, entityLabel: nm }));
    } else if (actionModal.input === 'sharedListNegative') { // F47
      const sid = sharedSetIdInput.trim(); const t = sharedNegText.trim();
      if (!/^\d+$/.test(sid)) { toast.error('Enter a numeric shared-set id.'); return; }
      if (!t) { toast.error('Enter a keyword to add.'); return; }
      ops = ops.map((o) => ({ ...o, sharedSetId: sid, text: t, matchType: sharedNegMatch }));
    } else if (actionModal.input === 'attachCampaign') { // F47
      const sid = sharedSetIdInput.trim();
      if (!/^\d+$/.test(sid)) { toast.error('Enter a numeric shared-set id.'); return; }
      if (!attachCampaignId) { toast.error('Pick a campaign to attach the list to.'); return; }
      ops = ops.map((o) => ({ ...o, sharedSetId: sid, campaignId: attachCampaignId, entityLabel: nameOfCampaign(attachCampaignId) || attachCampaignId }));
    } else if (actionModal.input === 'biddingStrategy') { // F26
      const strat = editBiddingStrategy;
      const op: any = { biddingStrategy: strat };
      if (strat === 'MAXIMIZE_CONVERSIONS' && editTargetCpa.trim()) {
        const cpa = parseFloat(editTargetCpa);
        if (!isFinite(cpa) || cpa <= 0) { toast.error('Target CPA must be a positive number (or blank).'); return; }
        op.targetCpaMicros = Math.round(cpa * 1e6);
      }
      if (strat === 'MAXIMIZE_CONVERSION_VALUE' && editTargetRoas.trim()) {
        const roas = parseFloat(editTargetRoas);
        if (!isFinite(roas) || roas <= 0) { toast.error('Target ROAS must be a positive number (or blank).'); return; }
        op.targetRoas = roas;
      }
      ops = ops.map((o) => ({ ...o, ...op }));
    } else if (actionModal.input === 'networks') {       // F26
      ops = ops.map((o) => ({ ...o, searchPartners: editSearchPartners, contentNetwork: editContentNetwork }));
    } else if (actionModal.input === 'deviceBid') {      // F26
      const pct = parseFloat(editDeviceModifier);
      if (!isFinite(pct) || pct < -100 || pct > 900) { toast.error('Enter a % between -100 and 900.'); return; }
      const mult = Math.round((1 + pct / 100) * 100) / 100;
      ops = ops.map((o) => ({ ...o, device: editDeviceType, bidModifier: mult }));
    } else if (actionModal.input === 'adSchedule') {     // F26
      const sh = parseInt(editScheduleStart, 10); const eh = parseInt(editScheduleEnd, 10);
      if (!isFinite(sh) || !isFinite(eh) || sh < 0 || eh > 24 || sh >= eh) { toast.error('Need 0 ≤ start < end ≤ 24.'); return; }
      const op: any = { dayOfWeek: editScheduleDay, startHour: sh, endHour: eh };
      if (editScheduleModifier.trim()) {
        const pct = parseFloat(editScheduleModifier);
        // Backend floor: schedule bid modifier mult must be 0.1–10 (no "exclude"
        // via 0, unlike device), i.e. -90..900%.
        if (!isFinite(pct) || pct < -90 || pct > 900) { toast.error('Bid modifier % must be between -90 and 900 (or blank).'); return; }
        op.bidModifier = Math.round((1 + pct / 100) * 100) / 100;
      }
      ops = ops.map((o) => ({ ...o, ...op }));
    } else if (actionModal.input === 'location') {       // F26
      const gid = editGeoId.trim();
      if (!/^\d+$/.test(gid)) { toast.error('Enter a numeric geoTargetConstant id (e.g. 2840 = US).'); return; }
      ops = ops.map((o) => ({ ...o, geoTargetId: gid }));
    } else if (actionModal.input === 'language') {       // F26
      const lid = editLanguageId.trim();
      if (!/^\d+$/.test(lid)) { toast.error('Pick a language.'); return; }
      ops = ops.map((o) => ({ ...o, languageId: lid }));
    }
    setActionApplying(true);
    try {
      if (actionModal.draftOnly) {
        // Bulk / high-volume action → DRAFT a pending_review change set (source
        // 'manual', NOT 'direct' — so it respects the kill switch and is human-
        // approved in "change activity" below, not auto-applied live).
        const r = await proposeChange(customerId, ops, { apply: false, rationale: actionModal.rationale, kind: actionModal.kind, source: 'manual' });
        if (!r.ok) throw new Error(r.error || 'failed');
        toast.success(`Drafted ${ops.length} change${ops.length === 1 ? '' : 's'} for review — approve them in “change activity” at the bottom of this page.`);
        actionModal.onApplied?.(r);
        setActionModal(null);
      } else {
        const r = await proposeChange(customerId, ops, { apply: true, rationale: actionModal.rationale, kind: actionModal.kind, source: 'direct' });
        if (!r.ok) throw new Error(r.error || 'failed');
        toast.success('Applied to Google Ads. Undo from “change activity” at the bottom of this page.');
        actionModal.onApplied?.(r);     // optimistic store-backed update (e.g. negative removal / inline-undo tracking)
        setWriteNonce((x) => x + 1);    // re-pull the live deep tables so the change shows now
        setActionModal(null);
      }
    } catch (e: any) {
      toast.error(`Couldn't ${actionModal.draftOnly ? 'draft' : 'apply'}: ${e?.message || 'error'}`);
    } finally {
      setActionApplying(false);
    }
  }, [actionModal, customerId, modalBid, modalKwText, modalKwMatch, modalPct,
    assetText, assetUrl, assetDesc1, assetDesc2, assetScope,
    sharedListName, sharedSetIdInput, sharedNegText, sharedNegMatch, attachCampaignId, nameOfCampaign,
    editBiddingStrategy, editTargetCpa, editTargetRoas, editSearchPartners, editContentNetwork,
    editDeviceType, editDeviceModifier, editScheduleDay, editScheduleStart, editScheduleEnd, editScheduleModifier,
    editGeoId, editLanguageId]);

  const openAddNegative = useCallback((t: SearchTermDoc) => {
    if (!t.campaignId) { toast.error('This term has no campaign.'); return; }
    const m = t.metrics30d || {};
    const docId = String((t as any).docId || '');
    const campaignId = String(t.campaignId);
    const text = String(t.searchTerm || '');
    setActionModal({
      title: 'Add negative keyword',
      lines: <>Block <strong>“{t.searchTerm}”</strong> [exact] on campaign <strong>{nameOfCampaign(t.campaignId) || t.campaignId}</strong>?</>,
      warn: LIVE_WARN,
      ops: [{ type: 'negativeKeyword.add', scope: 'campaign', campaignId, text, matchType: 'EXACT', entityLabel: nameOfCampaign(t.campaignId) }],
      kind: 'negativeKeyword.add',
      rationale: `Search term "${t.searchTerm}" spent ${fmtCur(n(m.cost), currency)} with ${n(m.conversions).toFixed(2)} conversions over ${rangeLabel}.`,
      // Record the applied negative so the row can offer a true inline undo.
      onApplied: (resp) => {
        const rn = String(resp?.applied?.resourceNames?.[0] || '');
        const mm = rn.match(/~(\d+)$/);              // …campaignCriteria/{campaignId}~{criterionId}
        const criterionId = mm ? mm[1] : '';
        if (docId && criterionId) {
          setAddedNegByTerm((prev) => { const map = new Map(prev); map.set(docId, { criterionId, campaignId, scope: 'campaign', text, matchType: 'EXACT' }); return map; });
        } else if (docId) {
          // Applied, but no resourceName came back → mark excluded optimistically so
          // the row doesn't mislead with "+ neg" again (no inline undo handle though).
          setExcludedNoUndo((prev) => { const s = new Set(prev); s.add(docId); return s; });
        }
      },
    });
  }, [nameOfCampaign, currency, rangeLabel]);

  // Bulk-draft EXACT campaign negatives for every actionable wasted term in the
  // current view (zero-conversion spend, not already excluded). One change set,
  // reviewed/approved like any other — the "act on it" target for the
  // wasted-search-term alert.
  const BULK_NEG_CAP = 200;
  const openBulkNegatives = useCallback(() => {
    const targets = filteredTerms.filter((t) => n(t.metrics30d?.cost) > 0 && n(t.metrics30d?.conversions) === 0 && !termBlocked(t) && t.campaignId);
    if (targets.length === 0) { toast.info('No actionable wasted terms (zero-conversion, not yet excluded) in this view.'); return; }
    // Dedupe by (campaign, term): the same query can appear under several ad
    // groups of one campaign, but a campaign negative blocks it once. Keep the
    // highest-spend instance for the rationale, then cap.
    const seen = new Set<string>();
    const deduped: typeof targets = [];
    for (const t of targets) {
      const k = `${t.campaignId}~${String(t.searchTerm || '').toLowerCase()}`;
      if (seen.has(k)) continue;
      seen.add(k); deduped.push(t);
    }
    const capped = deduped.slice(0, BULK_NEG_CAP);
    const totalCost = capped.reduce((s, t) => s + n(t.metrics30d?.cost), 0);
    const ops = capped.map((t) => ({ type: 'negativeKeyword.add', scope: 'campaign', campaignId: String(t.campaignId), text: String(t.searchTerm || ''), matchType: 'EXACT', entityLabel: nameOfCampaign(t.campaignId) }));
    setActionModal({
      title: `Draft negatives for ${capped.length} wasted term${capped.length === 1 ? '' : 's'}`,
      lines: <>Draft EXACT campaign negatives for <strong>{capped.length}</strong> term{capped.length === 1 ? '' : 's'} that spent <strong>{fmtCur(totalCost, currency)}</strong> with zero conversions ({rangeLabel})? Each is blocked on its own campaign. This creates a proposal you review &amp; approve below — nothing is applied yet.{deduped.length > capped.length ? <> <span style={{ color: '#d8a857' }}>(Top {capped.length} of {deduped.length} by spend — re-run for the rest.)</span></> : null}</>,
      ops,
      kind: 'negativeKeyword.add',
      draftOnly: true,
      rationale: `Bulk negatives: ${capped.length} search terms spent ${fmtCur(totalCost, currency)} with 0 conversions over ${rangeLabel}.`,
    });
  }, [filteredTerms, termBlocked, nameOfCampaign, currency, rangeLabel]);

  // Inline undo of a search-term negative added THIS SESSION — a direct
  // negativeKeyword.remove (same gate model as the add: confirm was the gate;
  // this reverses it without a second modal). Clears the session record + re-pulls.
  const undoNegative = useCallback(async (docId: string) => {
    const rec = addedNegByTerm.get(docId);
    if (!rec) return;
    setUndoingTerm(docId);
    try {
      const ops = [{ type: 'negativeKeyword.remove', scope: rec.scope, campaignId: rec.campaignId, criterionId: rec.criterionId, text: rec.text, matchType: rec.matchType, entityLabel: nameOfCampaign(rec.campaignId) }];
      const r = await proposeChange(customerId, ops, { apply: true, kind: 'negativeKeyword.remove', source: 'direct', rationale: 'Undo: remove the negative just added from this search term.' });
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success('Undone — the term can serve again.');
      setAddedNegByTerm((prev) => { const map = new Map(prev); map.delete(docId); return map; });
      setWriteNonce((x) => x + 1);
    } catch (e: any) {
      toast.error(`Couldn't undo: ${e?.message || 'error'}`);
    } finally {
      setUndoingTerm('');
    }
  }, [addedNegByTerm, customerId, nameOfCampaign]);

  const toggleStatus = useCallback((kind: 'keyword.setStatus' | 'ad.setStatus' | 'adGroup.setStatus', cur: any, label: string, idFields: any) => {
    const c = String(cur || 'ENABLED').toUpperCase() === 'PAUSED' ? 'PAUSED' : 'ENABLED';
    const next = c === 'PAUSED' ? 'ENABLED' : 'PAUSED';
    const verb = next === 'PAUSED' ? 'Pause' : 'Enable';
    const noun = kind === 'keyword.setStatus' ? 'keyword' : kind === 'ad.setStatus' ? 'ad' : 'ad group';
    setActionModal({
      title: `${verb} ${noun}`,
      lines: <>{verb} {noun} <strong>“{label}”</strong>?</>,
      warn: LIVE_WARN,
      ops: [{ type: kind, ...idFields, status: next, beforeStatus: c, entityLabel: label }],
      kind,
    });
  }, []);

  const openRemoveNegative = useCallback((nz: any) => {
    if (!nz.criterionId) { toast.error('Re-sync this account first (this negative is missing its criterion id).'); return; }
    setActionModal({
      title: 'Remove negative keyword',
      lines: <>Remove negative <strong>“{nz.text}”</strong> from <strong>{nz.campaignName || nz.adGroupName || 'this account'}</strong>?</>,
      warn: 'This un-blocks the term — it can serve and spend again. ' + LIVE_WARN,
      danger: true,
      ops: [{ type: 'negativeKeyword.remove', scope: nz.scope === 'adGroup' ? 'adGroup' : 'campaign', campaignId: nz.campaignId, adGroupId: nz.adGroupId, criterionId: nz.criterionId, text: nz.text, matchType: nz.matchType, entityLabel: nz.campaignName || nz.adGroupName }],
      kind: 'negativeKeyword.remove',
      onApplied: () => setRemovedNegDocIds((prev) => { const s = new Set(prev); s.add(String(nz.docId)); return s; }),
    });
  }, []);

  // ── F27: add a sitelink / callout extension ──────────────────────
  const openAddSitelink = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign to add a sitelink.'); return; }
    setAssetText(''); setAssetUrl(''); setAssetDesc1(''); setAssetDesc2('');
    setActionModal({
      title: 'Add sitelink',
      lines: <>Add sitelink to campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>:</>,
      warn: LIVE_WARN, input: 'sitelink',
      ops: [{ type: 'asset.addSitelink', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'asset.addSitelink',
    });
  }, [campFilter, nameOfCampaign]);

  /** Detach an existing extension. The asset stays in the account library, so
   *  this is reversible from "change activity" — unlike a campaign/ad-group
   *  remove. Identified by assetId + fieldType, never by label: two sitelinks
   *  can share a label and differ only by destination. */
  const openRemoveAsset = useCallback((a: AssetDoc) => {
    const level = a.level === 'customer' ? 'customer' : a.level === 'adGroup' ? 'adGroup' : 'campaign';
    const parentId = level === 'adGroup' ? (a.adGroupId || '') : level === 'campaign' ? (a.campaignId || '') : '';
    if (level !== 'customer' && !parentId) {
      toast.error('This extension row is missing its campaign/ad-group id — re-sync the account and try again.');
      return;
    }
    const where = level === 'campaign' ? (a.campaignName || 'this campaign')
      : level === 'adGroup' ? (a.adGroupName || 'this ad group') : 'the whole account';
    setActionModal({
      title: `Remove ${lower(a.fieldType) || 'extension'}`,
      lines: (
        <>
          Detach <strong>{a.text || a.assetId}</strong>
          {a.finalUrl ? <> (<span style={{ wordBreak: 'break-all' }}>{a.finalUrl}</span>)</> : null}
          {' '}from <strong>{where}</strong>. It stops showing on your ads.
          <br />
          <span className="text-muted">The asset stays in your account library, so this can be undone from “change activity”.</span>
        </>
      ),
      warn: LIVE_WARN,
      ops: [{
        type: 'asset.remove',
        assetId: a.assetId,
        fieldType: a.fieldType,
        assetLevel: level,
        ...(level === 'campaign' ? { campaignId: parentId } : {}),
        ...(level === 'adGroup' ? { adGroupId: parentId } : {}),
        entityLabel: [a.text, a.finalUrl].filter(Boolean).join(' → ') || String(a.assetId || ''),
      }],
      kind: 'asset.remove',
    });
  }, []);

  const openAddCallout = useCallback(() => {
    setAssetText(''); setAssetScope(campFilter ? 'campaign' : 'account');
    setActionModal({
      title: 'Add callout',
      lines: <>Add callout {campFilter ? <>to campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong></> : <>to the whole account</>}:</>,
      warn: LIVE_WARN, input: 'callout',
      ops: [{ type: 'asset.addCallout', ...(campFilter ? { campaignId: campFilter } : {}), entityLabel: campFilter ? (nameOfCampaign(campFilter) || campFilter) : 'account' }],
      kind: 'asset.addCallout',
    });
  }, [campFilter, nameOfCampaign]);

  // ── F47: shared negative-list management ─────────────────────────
  const openCreateSharedList = useCallback(() => {
    setSharedListName('');
    setActionModal({
      title: 'Create shared negative list',
      lines: <>Create an account-wide shared negative-keyword list. After it's created (and synced), attach it to campaigns and add negatives to it below.</>,
      warn: LIVE_WARN, input: 'sharedListName',
      ops: [{ type: 'sharedList.create' }],
      kind: 'sharedList.create',
    });
  }, []);

  const openAddSharedNegative = useCallback(() => {
    setSharedSetIdInput(''); setSharedNegText(''); setSharedNegMatch('EXACT');
    setActionModal({
      title: 'Add negative to shared list',
      lines: <>Add a negative keyword to an existing shared list (by its numeric id). Find the id in Google Ads → Tools → Shared library → Negative keyword lists.</>,
      warn: LIVE_WARN, input: 'sharedListNegative',
      ops: [{ type: 'sharedList.addNegative' }],
      kind: 'sharedList.addNegative',
    });
  }, []);

  const openAttachSharedList = useCallback(() => {
    setSharedSetIdInput(''); setAttachCampaignId(campFilter || '');
    setActionModal({
      title: 'Attach shared list to campaign',
      lines: <>Attach an existing shared negative list (by its numeric id) to a campaign. The list's negatives then apply to that campaign automatically.</>,
      warn: LIVE_WARN, input: 'attachCampaign',
      ops: [{ type: 'sharedList.attachCampaign' }],
      kind: 'sharedList.attachCampaign',
    });
  }, [campFilter]);

  const openKeywordBid = useCallback((k: KeywordDoc) => {
    setModalBid(((k.cpcBidMicros || 0) / 1e6).toFixed(2));
    setActionModal({
      title: 'Set keyword bid',
      lines: <>Set the max CPC for keyword <strong>“{k.text}”</strong>:</>,
      warn: LIVE_WARN, input: 'bid',
      ops: [{ type: 'keyword.setBid', adGroupId: k.adGroupId, criterionId: k.criterionId, beforeBidMicros: k.cpcBidMicros || undefined, entityLabel: k.text }],
      kind: 'keyword.setBid',
    });
  }, []);

  const openAdGroupBid = useCallback((ag: AdGroupDoc) => {
    setModalBid(((ag.cpcBidMicros || 0) / 1e6).toFixed(2));
    setActionModal({
      title: 'Set ad-group bid',
      lines: <>Set the default max CPC for ad group <strong>“{ag.name || ag.id}”</strong>:</>,
      warn: LIVE_WARN, input: 'bid',
      ops: [{ type: 'adGroup.setBid', adGroupId: ag.id, beforeBidMicros: ag.cpcBidMicros || undefined, entityLabel: ag.name || ag.id }],
      kind: 'adGroup.setBid',
    });
  }, []);

  const openAddKeyword = useCallback(() => {
    if (!agFilter) { toast.error('Pick an ad group (the filter above the table) to add a keyword to.'); return; }
    setModalKwText(''); setModalKwMatch('PHRASE');
    setActionModal({
      title: 'Add keyword',
      lines: <>Add an enabled keyword to ad group <strong>{nameOfAdGroup(agFilter)}</strong>:</>,
      warn: LIVE_WARN, input: 'keyword',
      ops: [{ type: 'keyword.add', adGroupId: agFilter, status: 'ENABLED', entityLabel: nameOfAdGroup(agFilter) }],
      kind: 'keyword.add',
    });
  }, [agFilter, nameOfAdGroup]);

  const openDeviceBid = useCallback((campaignId: string, campaignName: string, device: string) => {
    if (!campaignId || !device) { toast.error('Missing campaign or device.'); return; }
    const dev = String(device).toUpperCase();
    setModalPct('');
    setActionModal({
      title: 'Set device bid adjustment',
      lines: <>Adjust the <strong>{dev.toLowerCase()}</strong> bid on campaign <strong>“{campaignName || campaignId}”</strong>:</>,
      warn: LIVE_WARN, input: 'pct',
      ops: [{ type: 'campaign.setDeviceBidModifier', campaignId: String(campaignId), device: dev, entityLabel: `${campaignName || campaignId} · ${dev.toLowerCase()}` }],
      kind: 'campaign.setDeviceBidModifier',
    });
  }, []);

  const openCreateAdGroup = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign (the filter above the table) to create the ad group in.'); return; }
    setModalKwText(''); setModalBid('');
    setActionModal({
      title: 'Create ad group',
      lines: <>Create a new ad group in campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>. It is created <strong>PAUSED</strong> — it won't serve or spend until you enable it.</>,
      warn: LIVE_WARN, input: 'adgroup',
      ops: [{ type: 'adGroup.create', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'adGroup.create',
      // A new paused ad group has no metrics in the range, so the range-based
      // table won't list it yet — set that expectation instead of looking broken.
      onApplied: () => toast.info('Created PAUSED in Google Ads. It will appear in this table once it has activity (enable it + add keywords/ads first).'),
    });
  }, [campFilter, nameOfCampaign]);

  // ── F26: edit an EXISTING campaign (bidding / networks / device / schedule /
  // location / language). Each is a single Stage-2 op routed through the confirm
  // modal. The campaign is snapshotted at open so a later filter change can't
  // retarget the op. Backend reads the live BEFORE state for undo.
  const openEditBidding = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditBiddingStrategy('MANUAL_CPC'); setEditTargetCpa(''); setEditTargetRoas('');
    setActionModal({
      title: 'Set bidding strategy',
      lines: <>Change the bidding strategy for campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>:</>,
      warn: LIVE_WARN, input: 'biddingStrategy',
      ops: [{ type: 'campaign.setBiddingStrategy', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.setBiddingStrategy',
    });
  }, [campFilter, nameOfCampaign]);

  const openEditNetworks = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditSearchPartners(false); setEditContentNetwork(false);
    setActionModal({
      title: 'Set ad networks',
      lines: <>Toggle the Search-Partners and Display/Content networks for campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>. Google Search itself always stays on.</>,
      warn: LIVE_WARN, input: 'networks',
      ops: [{ type: 'campaign.setNetworks', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.setNetworks',
    });
  }, [campFilter, nameOfCampaign]);

  const openEditDeviceBid = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditDeviceType('MOBILE'); setEditDeviceModifier('');
    setActionModal({
      title: 'Set device bid adjustment',
      lines: <>Adjust a device-level bid for campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>:</>,
      warn: LIVE_WARN, input: 'deviceBid',
      ops: [{ type: 'campaign.setDeviceBidModifier', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.setDeviceBidModifier',
    });
  }, [campFilter, nameOfCampaign]);

  const openEditSchedule = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditScheduleDay('MONDAY'); setEditScheduleStart('8'); setEditScheduleEnd('18'); setEditScheduleModifier('');
    setActionModal({
      title: 'Add ad schedule',
      lines: <>Add an ad-schedule block to campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>. This ADDS a new block (it does not replace existing schedules).</>,
      warn: LIVE_WARN, input: 'adSchedule',
      ops: [{ type: 'campaign.addAdSchedule', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.addAdSchedule',
    });
  }, [campFilter, nameOfCampaign]);

  const openEditLocation = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditGeoId('');
    setActionModal({
      title: 'Add location target',
      lines: <>Add a location target to campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>. Requires a numeric geoTargetConstant id (e.g. 2840 = US, 2826 = UK, 2036 = Australia).</>,
      warn: LIVE_WARN, input: 'location',
      ops: [{ type: 'campaign.addLocation', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.addLocation',
    });
  }, [campFilter, nameOfCampaign]);

  const openEditLanguage = useCallback(() => {
    if (!campFilter) { toast.error('Pick a campaign first.'); return; }
    setEditCampaign({ id: campFilter, name: nameOfCampaign(campFilter) || campFilter });
    setEditLanguageId('1000');
    setActionModal({
      title: 'Add language target',
      lines: <>Add a language target to campaign <strong>{nameOfCampaign(campFilter) || campFilter}</strong>:</>,
      warn: LIVE_WARN, input: 'language',
      ops: [{ type: 'campaign.addLanguage', campaignId: campFilter, entityLabel: nameOfCampaign(campFilter) || campFilter }],
      kind: 'campaign.addLanguage',
    });
  }, [campFilter, nameOfCampaign]);

  const openCreateAd = useCallback(() => {
    if (!agFilter) { toast.error('Pick an ad group (the filter above the table) to create the ad in.'); return; }
    setRsaTarget({ id: agFilter, name: nameOfAdGroup(agFilter) || agFilter });
    setRsaUrl(''); setRsaPath1(''); setRsaPath2('');
    setRsaHeads([{ text: '', pin: '' }, { text: '', pin: '' }, { text: '', pin: '' }]);
    setRsaDescs([{ text: '', pin: '' }, { text: '', pin: '' }]);
    setRsaOpen(true);
  }, [agFilter, nameOfAdGroup]);

  const applyRsa = useCallback(async () => {
    const heads = rsaHeads.map((h) => ({ ...h, text: h.text.trim() })).filter((h) => h.text);
    const descs = rsaDescs.map((d) => ({ ...d, text: d.text.trim() })).filter((d) => d.text);
    const url = rsaUrl.trim();
    if (!/^https?:\/\/.+\..+/.test(url)) { toast.error('Enter a valid landing-page URL (https://…).'); return; }
    if (heads.length < 3) { toast.error('At least 3 headlines.'); return; }
    if (descs.length < 2) { toast.error('At least 2 descriptions.'); return; }
    if (heads.some((h) => h.text.length > 30)) { toast.error('A headline is over 30 characters.'); return; }
    if (descs.some((d) => d.text.length > 90)) { toast.error('A description is over 90 characters.'); return; }
    if (!rsaTarget.id) { toast.error('No target ad group — reopen the panel.'); return; }
    const op = {
      type: 'ad.createRSA', adGroupId: rsaTarget.id,
      finalUrl: url,
      headlines: heads.map((h) => (h.pin ? { text: h.text, pinnedField: h.pin } : h.text)),
      descriptions: descs.map((d) => (d.pin ? { text: d.text, pinnedField: d.pin } : d.text)),
      ...(rsaPath1.trim() ? { path1: rsaPath1.trim() } : {}),
      ...(rsaPath1.trim() && rsaPath2.trim() ? { path2: rsaPath2.trim() } : {}),   // path2 only with path1
      entityLabel: rsaTarget.name,
    };
    setRsaApplying(true);
    try {
      const r = await proposeChange(customerId, [op], { apply: true, kind: 'ad.createRSA', source: 'direct' });
      if (!r.ok) throw new Error(r.error || 'failed');
      toast.success('Ad created PAUSED in Google Ads — enable it when ready. Undo from “change activity” at the bottom of this page.');
      setWriteNonce((x) => x + 1);
      setRsaOpen(false);
    } catch (e: any) {
      toast.error(`Couldn't create the ad: ${e?.message || 'error'}`);
    } finally {
      setRsaApplying(false);
    }
  }, [rsaHeads, rsaDescs, rsaUrl, rsaPath1, rsaPath2, rsaTarget, customerId]);

  const openApplyRecommendation = useCallback((r: RecommendationDoc) => {
    if (!r.resourceName) { toast.error('This recommendation has no resource name — re-sync recommendations first.'); return; }
    const dc = n(r.impact?.deltaConversions); const dcost = n(r.impact?.deltaCost);
    const impactStr = r.impact?.hasImpact
      ? `${dc >= 0 ? '+' : ''}${dc.toFixed(1)} conv / ${dcost >= 0 ? '+' : ''}${dcost.toFixed(0)} cost (Google's estimate)`
      : 'no estimate provided';
    const highRisk = String(r.risk || '') === 'high';
    const campName = nameOfCampaign(r.campaignId);
    setActionModal({
      title: 'Apply Google recommendation',
      lines: (
        <>
          <div>Apply <strong>{recLabelF(r.type)}</strong>{campName ? <> on <strong>{campName}</strong></> : null} using Google's recommended values.</div>
          {r.detail?.summary ? <div style={{ marginTop: 6, opacity: 0.85 }}>{r.detail.summary}</div> : null}
          <div style={{ marginTop: 6, opacity: 0.85 }}>Estimated impact: {impactStr}.</div>
        </>
      ),
      warn: (
        <>
          <strong>This applies immediately and CANNOT be undone from here</strong> — recommendations have no dry-run. Manage the result in Google Ads afterwards.
          {highRisk ? ' This is a HIGH-RISK recommendation (spend / targeting / bidding) — review carefully.' : ''}
        </>
      ),
      danger: true,
      ops: [{
        type: 'recommendation.apply',
        resourceName: r.resourceName,
        recType: r.type,
        // campaign/adGroup let the backend re-resolve the rec's CURRENT handle
        // live at apply time (the stored resourceName goes stale between syncs,
        // which Google rejects with an unmappable requestError.UNKNOWN).
        campaignId: r.campaignId,
        adGroupId: r.adGroupId,
        recSummary: `${recLabelF(r.type)}${campName ? ' · ' + campName : ''}`,
        entityLabel: campName || recLabelF(r.type),
      }],
      kind: 'recommendation.apply',
    });
  }, [nameOfCampaign]);

  const openDismissRecommendation = useCallback((r: RecommendationDoc) => {
    if (!r.resourceName) { toast.error('This recommendation has no resource name — re-sync recommendations first.'); return; }
    const campName = nameOfCampaign(r.campaignId);
    setActionModal({
      title: 'Dismiss Google recommendation',
      lines: (
        <>
          <div>Dismiss <strong>{recLabelF(r.type)}</strong>{campName ? <> on <strong>{campName}</strong></> : null} in Google Ads.</div>
          {r.detail?.summary ? <div style={{ marginTop: 6, opacity: 0.85 }}>{r.detail.summary}</div> : null}
          <div style={{ marginTop: 6, opacity: 0.85 }}>This hides Google's suggestion — it does <strong>not</strong> change your campaigns, ads, or spend.</div>
        </>
      ),
      warn: (
        <>
          Dismissing only removes the suggestion from your recommendations list. There is no un-dismiss, but a future nightly sync re-surfaces it if Google re-issues it.
        </>
      ),
      danger: false,
      // Manual owner action → applies DIRECTLY (no draft/approve step). Dismiss is
      // non-destructive, and queuing it only let the recommendation go stale (→
      // RESOURCE_NOT_FOUND on approval). The AGENT path still force-queues a
      // dismissal for the owner to approve — that gating lives in the broker.
      ops: [{
        type: 'recommendation.dismiss',
        resourceName: r.resourceName,
        recType: r.type,
        // campaign/adGroup let the backend re-resolve the rec's CURRENT handle
        // live at dismiss time (the stored resourceName goes stale between syncs).
        campaignId: r.campaignId,
        adGroupId: r.adGroupId,
        recSummary: `${recLabelF(r.type)}${campName ? ' · ' + campName : ''}`,
        entityLabel: campName || recLabelF(r.type),
      }],
      kind: 'recommendation.dismiss',
    });
  }, [nameOfCampaign]);

  return (
    <section className="gads-deep" ref={rootRef}>
      <div className="gads-deep__bar">
        <div className="gads-deep__tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`gads-deep__tab${tab === t.key ? ' gads-deep__tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}{t.count !== undefined && <span className="gads-deep__tab-count">{t.count}</span>}
            </button>
          ))}
        </div>
        <div className="gads-deep__filters">
          <input
            type="search"
            className="gads-deep__search"
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
            placeholder={`search ${tabs.find((t) => t.key === tab)?.label || ''}…`}
            title="Filter the current tab by text (term, keyword, headline, name, URL…)"
          />
          <select
            className="gads-bar__picker"
            value={campFilter}
            onChange={(e) => { setCampFilter(e.target.value); setAgFilter(''); }}
            title="Filter by campaign"
          >
            <option value="">all campaigns</option>
            {campaigns.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.id}</option>
            ))}
          </select>
          <select
            className="gads-bar__picker"
            value={agFilter}
            onChange={(e) => setAgFilter(e.target.value)}
            title="Filter by ad group"
            disabled={agOptions.length === 0}
          >
            <option value="">all ad groups</option>
            {agOptions.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </select>
          {/* F26: edit the campaign selected in the filter above. An action menu
              that opens a per-setting modal — leaves the existing pause/budget
              controls in GoogleAdsPage untouched. Disabled until a campaign is
              picked (every op is campaign-scoped). */}
          <select
            className="gads-bar__picker"
            value=""
            disabled={!campFilter}
            title={campFilter ? 'Edit the selected campaign (drafts to the live account via the confirm modal)' : 'Pick a campaign to edit it'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'bidding') openEditBidding();
              else if (v === 'networks') openEditNetworks();
              else if (v === 'device') openEditDeviceBid();
              else if (v === 'schedule') openEditSchedule();
              else if (v === 'location') openEditLocation();
              else if (v === 'language') openEditLanguage();
            }}
          >
            <option value="">edit campaign…</option>
            <option value="bidding">bidding strategy</option>
            <option value="networks">networks</option>
            <option value="device">device bid</option>
            <option value="schedule">ad schedule</option>
            <option value="location">add location</option>
            <option value="language">add language</option>
          </select>
          <button type="button" className="gads-bar__reload" onClick={syncThis} disabled={syncing || fullSyncing}>
            {syncing ? '[syncing…]' : '[sync this account]'}
          </button>
          <button
            type="button"
            className="gads-bar__reload"
            onClick={fullSyncThis}
            disabled={syncing || fullSyncing}
            title="Pull the COMPLETE daily history (back to ~36 months) into the store so every timeframe is instant. Read-only; quota-cheap. The nightly limited sync is unaffected."
          >
            {fullSyncing ? `[full sync · ${fullSyncMsg || 'working…'}]` : '[full sync history]'}
          </button>
          <button
            type="button"
            className="gads-deep__audit-btn"
            onClick={runAudit}
            disabled={auditing}
            title="Run an AI audit of this account using the synced data"
          >
            {auditing ? '✦ auditing…' : '✦ AI audit'}
          </button>
        </div>
      </div>

      {/* ── F48: range-pull error banner (retry + conditional reconnect) ──
          Lifts a swallowed table error to an actionable banner. Always rendered
          OUTSIDE the tab conditionals so it's visible regardless of the tab. */}
      {rangePullError && (
        <div className="gads-alert gads-alert--warn" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>⚠ Couldn't load this data: {rangePullError.msg}</span>
          <button type="button" className="gads-deep__act-btn" onClick={handleRangeRetry} title="Re-pull this data for the selected window">retry</button>
          {suggestReconnect && (
            <button type="button" className="gads-alert__reconnect" onClick={handleReconnect} disabled={reconnecting}>
              {reconnecting ? 'reconnecting…' : 'Reconnect Google Ads'}
            </button>
          )}
          <button type="button" className="gads-deep__act-btn" onClick={() => setRangePullError(null)} title="Dismiss">dismiss</button>
        </div>
      )}

      {/* ── AI Account Audit panel ───────────────────────────────── */}
      {(auditing || audit || auditError) && (
        <AuditPanel
          loading={auditing}
          error={auditError}
          data={audit}
          onClose={() => { setAudit(null); setAuditError(''); }}
        />
      )}

      {/* ── Search terms (waste finder) ──────────────────────────── */}
      {tab === 'searchTerms' && (
        <div>
          {waste.count > 0 && (
            <div className="gads-deep__waste">
              ⚠ <strong>{fmtCur(waste.cost, currency)}</strong> wasted on{' '}
              <strong>{waste.count}</strong> search term{waste.count === 1 ? '' : 's'} with zero conversions, not yet excluded
              {' '}({rangeLabel}){campFilter || agFilter || textQuery || termStatus !== 'all' || wasteOnly ? ', in this filter' : ''}.
            </div>
          )}
          {waste.blockedCount > 0 && (
            <div style={{ color: '#6a6a6a', fontSize: 12, margin: '4px 0 0' }}>
              {fmtCur(waste.blockedCost, currency)} across {waste.blockedCount} zero-conversion term{waste.blockedCount === 1 ? '' : 's'} already excluded — historical spend from before the negative; nothing to action.
            </div>
          )}
          {reconcile && !agFilter && (reconcile.unattributed.clicks > 0 || reconcile.unattributed.cost > 0.005) && (() => {
            const ckpt = (b: { cost: number; clicks: number }) => `${fmtInt(b.clicks)} click${b.clicks === 1 ? '' : 's'} · ${fmtCur(b.cost, currency)}`;
            const parts = [
              { on: reconcile.pmax.clicks > 0 || reconcile.pmax.cost > 0.005, label: 'Performance Max', b: reconcile.pmax },
              { on: reconcile.display.clicks > 0 || reconcile.display.cost > 0.005, label: 'Display network', b: reconcile.display },
              { on: reconcile.youtube.clicks > 0 || reconcile.youtube.cost > 0.005, label: 'YouTube', b: reconcile.youtube },
              { on: reconcile.other.clicks > 0 || reconcile.other.cost > 0.005, label: 'other networks', b: reconcile.other },
              { on: reconcile.lowVolume.clicks > 0 || reconcile.lowVolume.cost > 0.005, label: 'low-volume / Search-Partners queries', b: reconcile.lowVolume },
            ].filter((p) => p.on);
            return (
              <div style={{ border: '1px solid #2a2a2a', borderLeft: '3px solid #d8a857', background: '#161310', borderRadius: 4, padding: '8px 12px', margin: '6px 0 10px', fontSize: 12.5, lineHeight: 1.6, color: '#bdbdbd' }}>
                <div>
                  Search terms account for <strong style={{ color: '#e8e8e8' }}>{fmtInt(reconcile.itemized.clicks)}</strong> of{' '}
                  <strong style={{ color: '#e8e8e8' }}>{fmtInt(reconcile.total.clicks)}</strong> clicks{' '}
                  (<strong style={{ color: '#e8e8e8' }}>{fmtCur(reconcile.itemized.cost, currency)}</strong> of{' '}
                  <strong style={{ color: '#e8e8e8' }}>{fmtCur(reconcile.total.cost, currency)}</strong>){campFilter ? ' for this campaign' : ''} this period.
                </div>
                <div style={{ marginTop: 2 }}>
                  The other <strong style={{ color: '#e8e8e8' }}>{fmtInt(reconcile.unattributed.clicks)} click{reconcile.unattributed.clicks === 1 ? '' : 's'}</strong> / {fmtCur(reconcile.unattributed.cost, currency)} have no Google-Search query to itemise:
                </div>
                <ul style={{ margin: '3px 0 0', paddingLeft: 18 }}>
                  {parts.map((p) => (<li key={p.label}>{p.label}: {ckpt(p.b)}</li>))}
                </ul>
                <div style={{ marginTop: 3, color: '#7a7a7a' }}>
                  Performance Max, Display and YouTube don’t produce search terms; low-volume Google queries are withheld by Google. Nothing is missing from the pipeline.
                </div>
              </div>
            );
          })()}
          {campNetPull.loading && searchTerms.length > 0 && (
            <div style={{ color: '#6a6a6a', fontSize: 12, margin: '4px 0 8px' }}>reconciling clicks &amp; spend by network…</div>
          )}
          {campNetPull.error && searchTerms.length > 0 && (
            <div style={{ color: '#d08770', fontSize: 12, margin: '4px 0 8px' }}>
              couldn’t reconcile clicks/spend by network ({campNetPull.error}) — search-term totals below cover only Google-Search queries, so they won’t match campaign totals.
            </div>
          )}
          <div className="gads-deep__subhead" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <select
              className="gads-bar__picker"
              value={termStatus}
              onChange={(e) => setTermStatus(e.target.value as any)}
              title="Filter by exclusion status (includes terms negated this session)"
            >
              <option value="all">all statuses</option>
              <option value="active">active only</option>
              <option value="excluded">excluded only</option>
            </select>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Only terms that spent money with zero conversions">
              <input type="checkbox" checked={wasteOnly} onChange={(e) => setWasteOnly(e.target.checked)} />
              <span>waste only</span>
            </label>
            {waste.count > 0 && (
              <button type="button" className="gads-deep__act-btn" onClick={openBulkNegatives} title={`Draft EXACT campaign negatives for the actionable wasted terms in this view (up to ${BULK_NEG_CAP}; review & approve before they apply)`}>
                draft negatives ({Math.min(waste.count, BULK_NEG_CAP)})
              </button>
            )}
            <span style={{ marginLeft: 'auto', color: '#6a6a6a' }}>{filteredTerms.length} term{filteredTerms.length === 1 ? '' : 's'}</span>
          </div>
          <DeepTable
            empty={searchTermsPull.loading ? 'loading…' : searchTermsPull.error ? `error: ${searchTermsPull.error}` : (searchTerms.length === 0 ? `No search terms in ${rangeLabel}.` : 'No search terms match this filter.')}
            rows={filteredTerms}
            cols={[
              { label: 'search term', sort: (t: SearchTermDoc) => String(t.searchTerm || '').toLowerCase() },
              { label: 'status', sort: (t: SearchTermDoc & { docId?: string }) => (termBlocked(t) ? 'excluded' : lower(t.status)) },
              { label: 'campaign', sort: (t: SearchTermDoc) => nameOfCampaign(t.campaignId).toLowerCase() },
              { label: 'cost', sort: (t: SearchTermDoc) => n(t.metrics30d?.cost) },
              { label: 'clicks', sort: (t: SearchTermDoc) => n(t.metrics30d?.clicks) },
              { label: 'conv.', sort: (t: SearchTermDoc) => n(t.metrics30d?.conversions) },
              '',
            ]}
            numCols={[3, 4, 5]}
            defaultSortIdx={3}
            render={(t: SearchTermDoc, i) => {
              const m = t.metrics30d || {};
              const wasted = n(m.cost) > 0 && n(m.conversions) === 0;
              const docId = String((t as any).docId || '');
              const sessionNeg = addedNegByTerm.get(docId);            // negated by us this session
              const statusExcluded = /exclud/i.test(String(t.status || '')) || excludedNoUndo.has(docId);
              const blocked = statusExcluded || !!sessionNeg;          // optimistic: just-added counts immediately
              return (
                <tr key={(t as any).docId ?? i} className={wasted && !blocked ? 'gads-deep__row--waste' : ''}>
                  <td className="name">{t.searchTerm}<TermSubline t={t} /></td>
                  <td className="status">{blocked ? 'excluded' : lower(t.status)}</td>
                  <td className="status">{nameOfCampaign(t.campaignId)}</td>
                  <td className="num">{fmtCur(n(m.cost), currency)}</td>
                  <td className="num">{fmtInt(n(m.clicks))}</td>
                  <td className="num">{n(m.conversions).toFixed(2)}</td>
                  <td className="status">
                    {sessionNeg ? (
                      <button
                        type="button"
                        className="gads-deep__act-btn gads-deep__act-btn--danger"
                        disabled={undoingTerm === docId}
                        title="Undo — remove the negative you just added (the term can serve again)"
                        onClick={() => undoNegative(docId)}
                      >
                        {undoingTerm === docId ? '…' : 'undo'}
                      </button>
                    ) : statusExcluded ? (
                      <span title="Already excluded — manage it in the Negatives tab">excluded</span>
                    ) : t.campaignId ? (
                      <button
                        type="button"
                        className="gads-deep__neg-btn"
                        title="Block this term as a campaign negative keyword (confirm, then applies to the live account)"
                        onClick={() => openAddNegative(t)}
                      >
                        + neg
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              );
            }}
          />
          {searchTermsPull.rows.length >= 500 && (
            <div className="gads-empty" style={{ marginTop: 6 }}>
              showing the top 500 terms by spend for this range — narrow the date range or filters to see deeper
            </div>
          )}
        </div>
      )}

      {/* ── Keywords ─────────────────────────────────────────────── */}
      {tab === 'keywords' && (
        <>
        <div className="gads-deep__subhead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>{agFilter ? `Add keywords to: ${nameOfAdGroup(agFilter) || agFilter}` : 'Tip: pick an ad group in the filter above to add a keyword.'}</span>
          <button type="button" className="gads-deep__act-btn" onClick={openAddKeyword} disabled={!agFilter} title={agFilter ? 'Add an enabled keyword to the selected ad group' : 'Select an ad group first'}>+ add keyword</button>
        </div>
        <DeepTable
          empty={keywordsPull.loading ? 'loading…' : keywordsPull.error ? `error: ${keywordsPull.error}` : (keywords.length === 0 ? `No keywords in ${rangeLabel}.` : 'No keywords match this filter.')}
          rows={filteredKeywords}
          cols={[
            { label: 'keyword', sort: (k: KeywordDoc) => String(k.text || '').toLowerCase() },
            { label: 'match', sort: (k: KeywordDoc) => lower(k.matchType) },
            // NaN sentinel: keywords without a QS sink to the bottom both ways.
            { label: 'qs', sort: (k: KeywordDoc) => (k.qualityScore == null ? NaN : Number(k.qualityScore)) },
            'rel / lp / ctr',
            { label: 'campaign', sort: (k: KeywordDoc) => nameOfCampaign(k.campaignId).toLowerCase() },
            { label: 'cost', sort: (k: KeywordDoc) => n(k.metrics30d?.cost) },
            { label: 'clicks', sort: (k: KeywordDoc) => n(k.metrics30d?.clicks) },
            { label: 'conv.', sort: (k: KeywordDoc) => n(k.metrics30d?.conversions) },
            '',
          ]}
          numCols={[2, 5, 6, 7]}
          defaultSortIdx={5}
          render={(k: KeywordDoc, i) => {
            const m = k.metrics30d || {};
            const qc = k.qsComponents;
            const compStr = qc ? `${qsShort(qc.adRelevance)} / ${qsShort(qc.landingPage)} / ${qsShort(qc.expectedCtr)}` : '—';
            const paused = String(k.status || '').toUpperCase() === 'PAUSED';
            const removed = isRemoved(k.status);
            return (
              <tr key={(k as any).docId ?? i} className={removed ? 'gads-deep__row--removed' : paused ? 'gads-deep__row--paused' : ''}>
                <td className="name">{k.text}{removed && <RemovedTag />}</td>
                <td className="status">{lower(k.matchType)}</td>
                <td className="num">{k.qualityScore ?? '—'}</td>
                <td className="status" title="ad relevance / landing-page experience / expected CTR">{compStr}</td>
                <td className="status">{nameOfCampaign(k.campaignId)}</td>
                <td className="num">{fmtCur(n(m.cost), currency)}</td>
                <td className="num">{fmtInt(n(m.clicks))}</td>
                <td className="num">{n(m.conversions).toFixed(2)}</td>
                <td className="status" style={{ whiteSpace: 'nowrap' }}>{removed ? removedActionsNote : k.criterionId && k.adGroupId ? (<>
                  <button type="button" className="gads-deep__act-btn" onClick={() => toggleStatus('keyword.setStatus', k.status, k.text || '', { adGroupId: k.adGroupId, criterionId: k.criterionId })}>{paused ? 'enable' : 'pause'}</button>{' '}
                  <button type="button" className="gads-deep__act-btn" onClick={() => openKeywordBid(k)} title="Set max CPC bid">bid</button>
                </>) : '—'}</td>
              </tr>
            );
          }}
        />
        </>
      )}

      {/* ── Ads ──────────────────────────────────────────────────── */}
      {tab === 'ads' && (
        <>
        <div className="gads-deep__subhead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }} title="Only ads with a policy problem (disapproved / limited)">
            <input type="checkbox" checked={adApproval === 'disapproved'} onChange={(e) => setAdApproval(e.target.checked ? 'disapproved' : 'all')} />
            <span>policy issues only</span>
          </label>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span>{agFilter ? `New ads go to: ${nameOfAdGroup(agFilter) || agFilter}` : 'Tip: pick an ad group in the filter above to create an ad.'}</span>
            <button type="button" className="gads-deep__act-btn" onClick={openCreateAd} disabled={!agFilter} title={agFilter ? 'Write a new responsive search ad (created paused)' : 'Select an ad group first'}>+ new ad</button>
          </span>
        </div>
        <DeepTable
          empty={adsPull.loading ? 'loading…' : adsPull.error ? `error: ${adsPull.error}` : (ads.length === 0 ? `No ads in ${rangeLabel}.` : 'No ads match this filter.')}
          rows={filteredAds}
          cols={[
            { label: 'ad (headlines)', sort: (a: AdDoc) => ((a.headlines || [])[0] || '').toLowerCase() },
            { label: 'type', sort: (a: AdDoc) => lower(a.type) },
            { label: 'strength', sort: (a: AdDoc) => lower(a.adStrength) },
            { label: 'policy', sort: (a: AdDoc) => lower(a.policy?.approvalStatus) },
            { label: 'final url', sort: (a: AdDoc) => ((a.finalUrls || [])[0] || '').toLowerCase() },
            { label: 'ad group', sort: (a: AdDoc) => nameOfAdGroup(a.adGroupId).toLowerCase() },
            { label: 'cost', sort: (a: AdDoc) => n(a.metrics30d?.cost) },
            { label: 'clicks', sort: (a: AdDoc) => n(a.metrics30d?.clicks) },
            { label: 'conv.', sort: (a: AdDoc) => n(a.metrics30d?.conversions) },
            '',
          ]}
          numCols={[6, 7, 8]}
          defaultSortIdx={6}
          render={(a: AdDoc, i) => {
            const m = a.metrics30d || {};
            const headline = (a.headlines || []).slice(0, 3).join(' · ') || '(no RSA headlines)';
            const approval = String(a.policy?.approvalStatus || '').toUpperCase();
            const policyBad = ['DISAPPROVED', 'APPROVED_LIMITED', 'AREA_OF_INTEREST_ONLY'].includes(approval);
            const url = (a.finalUrls || [])[0] || '';
            const paused = String(a.status || '').toUpperCase() === 'PAUSED';
            const removed = isRemoved(a.status);
            return (
              <tr key={(a as any).docId ?? i} className={removed ? 'gads-deep__row--removed' : paused ? 'gads-deep__row--paused' : ''}>
                <td className="name" title={(a.headlines || []).join(' | ')}>{headline}{removed && <RemovedTag />}</td>
                <td className="status">{lower(a.type)}</td>
                <td className="status">{lower(a.adStrength) || '—'}</td>
                <td className={policyBad ? 'status gads-deep__bad' : 'status'} title={(a.policy?.topics || []).map((t) => t.topic).join(', ')}>{a.policy ? lower(a.policy.approvalStatus) || '—' : '—'}</td>
                <td className="status">{url ? <a href={url} target="_blank" rel="noopener noreferrer" className="gads-deep__url" title={url}>{url.replace(/^https?:\/\//, '').slice(0, 40)}</a> : '—'}</td>
                <td className="status">{nameOfAdGroup(a.adGroupId)}</td>
                <td className="num">{fmtCur(n(m.cost), currency)}</td>
                <td className="num">{fmtInt(n(m.clicks))}</td>
                <td className="num">{n(m.conversions).toFixed(2)}</td>
                <td className="status">{removed ? removedActionsNote : a.id && a.adGroupId ? <button type="button" className="gads-deep__act-btn" onClick={() => toggleStatus('ad.setStatus', a.status, headline, { adGroupId: a.adGroupId, adId: a.id })}>{paused ? 'enable' : 'pause'}</button> : '—'}</td>
              </tr>
            );
          }}
        />
        </>
      )}

      {/* ── Ad groups ────────────────────────────────────────────── */}
      {tab === 'adGroups' && (
        <>
        <div className="gads-deep__subhead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>{campFilter ? `New ad groups go to: ${nameOfCampaign(campFilter) || campFilter}` : 'Tip: pick a campaign in the filter above to create an ad group.'}</span>
          <button type="button" className="gads-deep__act-btn" onClick={openCreateAdGroup} disabled={!campFilter} title={campFilter ? 'Create a new (paused) ad group in the selected campaign' : 'Select a campaign first'}>+ new ad group</button>
        </div>
        <DeepTable
          empty={adGroupsPull.loading ? 'loading…' : adGroupsPull.error ? `error: ${adGroupsPull.error}` : (adGroups.length === 0 ? `No ad groups in ${rangeLabel}.` : 'No ad groups match this filter.')}
          rows={filteredAdGroups}
          cols={[
            { label: 'ad group', sort: (ag: AdGroupDoc) => String(ag.name || '').toLowerCase() },
            { label: 'status', sort: (ag: AdGroupDoc) => lower(ag.status) },
            { label: 'campaign', sort: (ag: AdGroupDoc) => nameOfCampaign(ag.campaignId).toLowerCase() },
            // null impression share = no data → NaN sentinel sinks it.
            { label: 'impr. share', sort: (ag: AdGroupDoc) => (ag.impressionShare?.search == null ? NaN : Number(ag.impressionShare.search)) },
            { label: 'cost', sort: (ag: AdGroupDoc) => n(ag.metrics30d?.cost) },
            { label: 'impr.', sort: (ag: AdGroupDoc) => n(ag.metrics30d?.impressions) },
            { label: 'clicks', sort: (ag: AdGroupDoc) => n(ag.metrics30d?.clicks) },
            { label: 'ctr', sort: (ag: AdGroupDoc) => { const i = n(ag.metrics30d?.impressions); return i > 0 ? n(ag.metrics30d?.clicks) / i : NaN; } },
            { label: 'conv.', sort: (ag: AdGroupDoc) => n(ag.metrics30d?.conversions) },
            '',
          ]}
          numCols={[3, 4, 5, 6, 7, 8]}
          defaultSortIdx={4}
          render={(ag: AdGroupDoc, i) => {
            const m = ag.metrics30d || {};
            const impr = n(m.impressions); const clicks = n(m.clicks);
            const paused = String(ag.status || '').toUpperCase() === 'PAUSED';
            const removed = isRemoved(ag.status);
            return (
              <tr key={(ag as any).docId ?? i} className={removed ? 'gads-deep__row--removed' : paused ? 'gads-deep__row--paused' : ''}>
                <td className="name">{ag.name}</td>
                <td className="status">{lower(ag.status)}</td>
                <td className="status">{nameOfCampaign(ag.campaignId)}</td>
                <td className="num" title="search impression share">{isLabel(ag.impressionShare?.search)}</td>
                <td className="num">{fmtCur(n(m.cost), currency)}</td>
                <td className="num">{fmtInt(impr)}</td>
                <td className="num">{fmtInt(clicks)}</td>
                <td className="num">{fmtPct(impr > 0 ? clicks / impr : 0)}</td>
                <td className="num">{n(m.conversions).toFixed(2)}</td>
                <td className="status" style={{ whiteSpace: 'nowrap' }}>{removed ? removedActionsNote : ag.id ? (<>
                  <button type="button" className="gads-deep__act-btn" onClick={() => toggleStatus('adGroup.setStatus', ag.status, ag.name || ag.id, { adGroupId: ag.id })}>{paused ? 'enable' : 'pause'}</button>{' '}
                  <button type="button" className="gads-deep__act-btn" onClick={() => openAdGroupBid(ag)} title="Set default max CPC">bid</button>
                </>) : '—'}</td>
              </tr>
            );
          }}
        />
        </>
      )}

      {/* ── Conversion actions (config + per-action performance · F25) ── */}
      {tab === 'conversions' && (
        <>
          <div className="gads-deep__subhead">
            Per-conversion-action performance for {rangeLabel}. Google restricts this report to conversion metrics — cost / clicks / impressions aren't available alongside <code>segments.conversion_action</code>.
            {conversionsPerfPull.loading ? ' · loading performance…' : conversionsPerfPull.error ? ` · couldn't load performance (${conversionsPerfPull.error})` : ''}
          </div>
          <DeepTable
            empty={conversionRows.length === 0 ? 'No conversion-action data yet — sync the account.' : 'No conversion actions match.'}
            rows={conversionRows.filter((c) => !tq || String(c.name || '').toLowerCase().includes(tq))}
            cols={[
              { label: 'name', sort: (c: ConvRow) => String(c.name || '').toLowerCase() },
              { label: 'category', sort: (c: ConvRow) => lower(c.category) },
              { label: 'type', sort: (c: ConvRow) => lower(c.type) },
              { label: 'status', sort: (c: ConvRow) => lower(c.status) },
              { label: 'counting', sort: (c: ConvRow) => lower(c.countingType) },
              { label: 'conv.', sort: (c: ConvRow) => c.conversions },
              { label: 'all conv.', sort: (c: ConvRow) => c.allConversions },
              { label: 'conv. value', sort: (c: ConvRow) => c.conversionsValue },
              { label: 'all conv. value', sort: (c: ConvRow) => c.allConversionsValue },
            ]}
            numCols={[5, 6, 7, 8]}
            defaultSortIdx={7}
            render={(c: ConvRow, i) => (
              <tr key={(c as any).docId ?? c.id ?? i}>
                <td className="name">{c.name}</td>
                <td className="status">{lower(c.category)}</td>
                <td className="status">{c.hasConfig ? lower(c.type) : '—'}</td>
                <td className="status">{c.hasConfig ? lower(c.status) : '—'}</td>
                <td className="status">{c.hasConfig ? lower(c.countingType) : '—'}</td>
                <td className="num">{c.conversions.toFixed(2)}</td>
                <td className="num">{c.allConversions.toFixed(2)}</td>
                <td className="num">{fmtCur(c.conversionsValue, currency)}</td>
                <td className="num">{fmtCur(c.allConversionsValue, currency)}</td>
              </tr>
            )}
          />
        </>
      )}

      {/* ── Negative keywords (existing exclusions) ──────────────── */}
      {tab === 'negatives' && (
        <>
        {/* F47: shared negative-list management. Additive — the existing
            campaign/ad-group negatives table below is unchanged. */}
        <div className="gads-deep__subhead">
          <div>
            <strong>Shared negative lists</strong> are account-wide and apply automatically to every campaign you attach them to. Changes take effect in the table below after the next sync. To <strong>remove</strong> a negative from a shared list, open Google Ads directly — this app does not support shared-list negative removal.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" className="gads-deep__act-btn" onClick={openCreateSharedList} title="Create a new account-wide shared negative-keyword list">+ shared list</button>
            <button type="button" className="gads-deep__act-btn" onClick={openAddSharedNegative} title="Add a negative keyword to an existing shared list (by id)">+ negative to list</button>
            <button type="button" className="gads-deep__act-btn" onClick={openAttachSharedList} title="Attach an existing shared list to a campaign (by id)">attach list to campaign</button>
          </div>
        </div>
        <DeepTable
          empty={negativeKeywords.length === 0 ? 'No negative keywords applied (synced nightly).' : 'No negatives match.'}
          rows={negativeKeywords
            .filter((nz: any) => !removedNegDocIds.has(String(nz.docId)))
            .filter((nz) => !tq || String(nz.text || '').toLowerCase().includes(tq))}
          cols={[
            { label: 'negative keyword', sort: (g: NegativeKeywordDoc) => String(g.text || '').toLowerCase() },
            { label: 'match', sort: (g: NegativeKeywordDoc) => lower(g.matchType) },
            { label: 'scope', sort: (g: NegativeKeywordDoc) => lower(g.scope) },
            { label: 'applied to', sort: (g: NegativeKeywordDoc) => String(g.campaignName || g.adGroupName || g.sharedSetName || '').toLowerCase() },
            '',
          ]}
          numCols={[]}
          render={(neg: NegativeKeywordDoc, i) => (
            <tr key={(neg as any).docId ?? i}>
              <td className="name">{neg.text}</td>
              <td className="status">{lower(neg.matchType)}</td>
              <td className="status">{lower(neg.scope)}</td>
              <td className="status">{neg.campaignName || neg.adGroupName || neg.sharedSetName || '—'}</td>
              <td className="status">{(neg.scope === 'campaign' || neg.scope === 'adGroup') && neg.criterionId ? <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => openRemoveNegative(neg)}>remove</button> : '—'}</td>
            </tr>
          )}
        />
        </>
      )}

      {/* ── Extensions / assets ──────────────────────────────────── */}
      {tab === 'assets' && (
        <>
        {/* F27: add sitelink / callout extensions. Sitelinks are campaign-scoped
            (pick a campaign in the filter above); callouts default to account-wide
            but scope to the filtered campaign when one is selected. */}
        <div className="gads-deep__subhead" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>{campFilter ? `New extensions target: ${nameOfCampaign(campFilter) || campFilter}` : 'Tip: pick a campaign in the filter above to add a sitelink. Callouts can be account-wide.'}</span>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <button type="button" className="gads-deep__act-btn" onClick={openAddSitelink} disabled={!campFilter} title={campFilter ? 'Add a sitelink to the selected campaign' : 'Select a campaign first (sitelinks are campaign-scoped)'}>+ sitelink</button>
            <button type="button" className="gads-deep__act-btn" onClick={openAddCallout} title={campFilter ? 'Add a callout to the selected campaign' : 'Add an account-wide callout'}>+ callout</button>
          </span>
        </div>
        <DeepTable
          empty={assets.length === 0 ? 'No extensions found (synced nightly).' : 'No extensions match.'}
          rows={assets.filter((a) => !tq || String(a.text || a.assetId || '').toLowerCase().includes(tq) || String(a.finalUrl || '').toLowerCase().includes(tq))}
          cols={[
            { label: 'extension', sort: (a: AssetDoc) => String(a.text || a.assetId || '').toLowerCase() },
            { label: 'destination', sort: (a: AssetDoc) => String(a.finalUrl || '').toLowerCase() },
            { label: 'type', sort: (a: AssetDoc) => lower(a.fieldType) },
            { label: 'status', sort: (a: AssetDoc) => lower(a.status) },
            { label: 'approval', sort: (a: AssetDoc) => lower(a.approvalStatus) },
            { label: 'applied to', sort: (a: AssetDoc) => String(a.campaignName || a.adGroupName || 'account').toLowerCase() },
            { label: '' },
          ]}
          numCols={[]}
          render={(a: AssetDoc, i) => {
            const bad = String(a.approvalStatus || '').toUpperCase() === 'DISAPPROVED';
            const appliedTo = a.level === 'campaign' ? (a.campaignName || 'campaign')
              : a.level === 'adGroup' ? (a.adGroupName || 'ad group')
              : 'account';
            // Two extensions with the same LABEL but different destinations is a
            // real and confusing state (a renamed page, a stale URL left behind).
            // Flag it so the right one can be picked without guessing.
            const dupText = !!a.text && assets.filter((x) => x.text === a.text && x.fieldType === a.fieldType).length > 1;
            return (
              <tr key={(a as any).docId ?? i}>
                <td className="name">
                  {a.text || a.assetId}
                  {dupText && <span className="gads-deep__dup" title="Another extension shares this exact label — check the destination to tell them apart."> dup</span>}
                </td>
                <td className="status">
                  {a.finalUrl
                    ? <a href={a.finalUrl} target="_blank" rel="noopener noreferrer" title={a.finalUrl}>{shortUrl(a.finalUrl)}</a>
                    : '—'}
                </td>
                <td className="status">{lower(a.fieldType)}</td>
                <td className="status">{lower(a.status)}</td>
                <td className={bad ? 'status gads-deep__bad' : 'status'}>{lower(a.approvalStatus) || '—'}</td>
                <td className="status">{appliedTo}</td>
                <td className="status" style={{ whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    className="gads-deep__act-btn gads-deep__act-btn--danger"
                    onClick={() => openRemoveAsset(a)}
                    title="Detach this extension (queued for approval). The asset stays in your account library, so this can be undone."
                  >
                    remove
                  </button>
                </td>
              </tr>
            );
          }}
        />
        </>
      )}

      {/* ── Google Recommendations (read-only review queue) ──────── */}
      {tab === 'recommendations' && (
        <>
          <div className="gads-deep__subhead">
            Google's own optimization recommendations. <strong>Apply</strong> applies the recommendation with Google's recommended values — it is immediate and <strong>cannot be undone here</strong> (no dry-run exists). Review high-risk items (broad match, search partners, budget &amp; bidding) carefully. <strong>Dismiss</strong> hides a suggestion in Google Ads (queued for your approval in “change activity” below) — it never changes your campaigns.
          </div>
          <DeepTable
            empty={recsSorted.length === 0 ? 'No Google recommendations (synced nightly).' : 'No recommendations match.'}
            rows={recsSorted.filter((r) => !tq
              || recLabelF(r.type).toLowerCase().includes(tq)
              || String(r.detail?.summary || '').toLowerCase().includes(tq))}
            cols={[
              { label: 'recommendation', sort: (r: RecommendationDoc) => recLabelF(r.type).toLowerCase() },
              { label: 'category', sort: (r: RecommendationDoc) => String(r.category || '').toLowerCase() },
              { label: 'risk', sort: (r: RecommendationDoc) => ({ safe: 0, review: 1, high: 2 } as Record<string, number>)[r.risk || 'review'] ?? 1 },
              'suggested change',
              { label: 'est. impact', sort: (r: RecommendationDoc) => (r.impact?.hasImpact ? n(r.impact?.deltaConversions) : NaN) },
              { label: 'campaign', sort: (r: RecommendationDoc) => nameOfCampaign(r.campaignId).toLowerCase() },
              '',
            ]}
            numCols={[]}
            render={(r: RecommendationDoc, i) => {
              const risk = String(r.risk || 'review');
              const dc = n(r.impact?.deltaConversions); const dcost = n(r.impact?.deltaCost);
              const impactStr = r.impact?.hasImpact
                ? `${dc >= 0 ? '+' : ''}${dc.toFixed(1)} conv / ${dcost >= 0 ? '+' : ''}${dcost.toFixed(0)}`
                : 'no estimate';
              return (
                <tr key={r.resourceName ?? i} className={risk === 'high' ? 'gads-deep__bad' : undefined}>
                  <td className="name">{recLabelF(r.type)}{r.dismissed ? ' (dismissed)' : ''}</td>
                  <td className="status">{r.category || '—'}</td>
                  <td className="status">{risk}</td>
                  <td className="wrap">{r.detail?.summary || '—'}</td>
                  <td className="status">{impactStr}</td>
                  <td className="status">{nameOfCampaign(r.campaignId) || '—'}</td>
                  <td className="status">{!r.dismissed && r.resourceName ? (
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <button type="button" className={`gads-deep__act-btn${risk === 'high' ? ' gads-deep__act-btn--danger' : ''}`} onClick={() => openApplyRecommendation(r)} title="Apply this recommendation in Google Ads (cannot be undone here)">apply</button>
                      <button type="button" className="gads-deep__act-btn" onClick={() => openDismissRecommendation(r)} title="Dismiss this recommendation in Google Ads — owner-approved in “change activity”, non-destructive">dismiss</button>
                    </div>
                  ) : '—'}</td>
                </tr>
              );
            }}
          />
        </>
      )}

      {/* ── Landing pages ────────────────────────────────────────── */}
      {tab === 'landingPages' && (
        <DeepTable
          empty={landingPull.loading ? 'loading…' : landingPull.error ? `error: ${landingPull.error}` : `No landing-page data in ${rangeLabel}.`}
          rows={landingPull.rows.filter((r) => {
            if (!tq) return true;
            const lpv = (r.landingPageView || r.landing_page_view || {}) as any;
            return String(lpv.unexpandedFinalUrl || lpv.unexpanded_final_url || '').toLowerCase().includes(tq);
          })}
          cols={[
            { label: 'landing page', sort: (r: SegRow) => { const lpv = (r.landingPageView || r.landing_page_view || {}) as any; return String(lpv.unexpandedFinalUrl || lpv.unexpanded_final_url || '').toLowerCase(); } },
            { label: 'clicks', sort: (r: SegRow) => segMetrics(r).clicks },
            { label: 'cost', sort: (r: SegRow) => segMetrics(r).cost },
            { label: 'conv.', sort: (r: SegRow) => segMetrics(r).conversions },
            { label: 'mobile-friendly', sort: (r: SegRow) => { const mf = r.metrics?.mobileFriendlyClicksPercentage; return mf == null ? NaN : Number(mf); } },
          ]}
          numCols={[1, 2, 3, 4]}
          defaultSortIdx={2}
          render={(r: SegRow, i) => {
            const sm = segMetrics(r);
            const lpv = r.landingPageView || r.landing_page_view || {};
            const url = lpv.unexpandedFinalUrl || lpv.unexpanded_final_url || '';
            const mf = r.metrics?.mobileFriendlyClicksPercentage;
            return (
              <tr key={i}>
                <td className="name">{url ? <a href={url} target="_blank" rel="noopener noreferrer" className="gads-deep__url" title={url}>{url.replace(/^https?:\/\//, '').slice(0, 60)}</a> : '—'}</td>
                <td className="num">{fmtInt(sm.clicks)}</td>
                <td className="num">{fmtCur(sm.cost, currency)}</td>
                <td className="num">{sm.conversions.toFixed(2)}</td>
                <td className="num">{mf == null ? '—' : fmtPct(Number(mf))}</td>
              </tr>
            );
          }}
        />
      )}

      {/* ── Device ───────────────────────────────────────────────── */}
      {tab === 'device' && (
        <DeepTable
          empty={devicePull.loading ? 'loading…' : devicePull.error ? `error: ${devicePull.error}` : `No device data in ${rangeLabel}.`}
          rows={devicePull.rows.filter((r) => !tq || String(((r.campaign || {}) as any).name || '').toLowerCase().includes(tq))}
          cols={[
            { label: 'campaign', sort: (r: SegRow) => String(((r.campaign || {}) as any).name || '').toLowerCase() },
            { label: 'device', sort: (r: SegRow) => lower((r.segments || {}).device) },
            { label: 'cost', sort: (r: SegRow) => segMetrics(r).cost },
            { label: 'clicks', sort: (r: SegRow) => segMetrics(r).clicks },
            { label: 'ctr', sort: (r: SegRow) => { const sm = segMetrics(r); return sm.impressions > 0 ? sm.clicks / sm.impressions : NaN; } },
            { label: 'conv.', sort: (r: SegRow) => segMetrics(r).conversions },
            '',
          ]}
          numCols={[2, 3, 4, 5]}
          defaultSortIdx={2}
          render={(r: SegRow, i) => {
            const sm = segMetrics(r);
            const ctr = sm.impressions > 0 ? sm.clicks / sm.impressions : 0;
            const cmp = (r.campaign || {}) as any;
            const dev = String((r.segments || {}).device || '').toUpperCase();
            const canBid = cmp.id && ['DESKTOP', 'MOBILE', 'TABLET'].includes(dev);
            return (
              <tr key={i}>
                <td className="name">{cmp.name || ''}</td>
                <td className="status">{lower((r.segments || {}).device)}</td>
                <td className="num">{fmtCur(sm.cost, currency)}</td>
                <td className="num">{fmtInt(sm.clicks)}</td>
                <td className="num">{fmtPct(ctr)}</td>
                <td className="num">{sm.conversions.toFixed(2)}</td>
                <td className="status">{canBid ? <button type="button" className="gads-deep__act-btn" title="Set this device's bid adjustment for the campaign" onClick={() => openDeviceBid(String(cmp.id), cmp.name || '', dev)}>bid adj</button> : '—'}</td>
              </tr>
            );
          }}
        />
      )}

      {/* ── Geo ──────────────────────────────────────────────────── */}
      {tab === 'geo' && (
        <DeepTable
          empty={geoPull.loading ? 'loading…' : geoPull.error ? `error: ${geoPull.error}` : `No geo data in ${rangeLabel}.`}
          rows={geoPull.rows.filter((r) => {
            if (!tq) return true;
            const seg = r.segments || {};
            const gv = (r.geographicView || r.geographic_view || {}) as any;
            const countryId = String(gv.countryCriterionId || gv.country_criterion_id || '');
            const loc = geoName(seg.geoTargetCity || seg.geo_target_city)
              || geoName(seg.geoTargetRegion || seg.geo_target_region)
              || (countryId ? (geoNameMap[countryId] || countryId) : '');
            return loc.toLowerCase().includes(tq) || String(((r.campaign || {}) as any).name || '').toLowerCase().includes(tq);
          })}
          cols={[
            { label: 'location', sort: (r: SegRow) => { const seg = r.segments || {}; const gv = (r.geographicView || r.geographic_view || {}) as any; const cid = String(gv.countryCriterionId || gv.country_criterion_id || ''); return (geoName(seg.geoTargetCity || seg.geo_target_city) || geoName(seg.geoTargetRegion || seg.geo_target_region) || (cid ? (geoNameMap[cid] || cid) : '')).toLowerCase(); } },
            { label: 'type', sort: (r: SegRow) => { const gv = (r.geographicView || r.geographic_view || {}) as any; return lower(gv.locationType || gv.location_type); } },
            { label: 'campaign', sort: (r: SegRow) => String(((r.campaign || {}) as any).name || '').toLowerCase() },
            { label: 'cost', sort: (r: SegRow) => segMetrics(r).cost },
            { label: 'clicks', sort: (r: SegRow) => segMetrics(r).clicks },
            { label: 'conv.', sort: (r: SegRow) => segMetrics(r).conversions },
          ]}
          numCols={[3, 4, 5]}
          defaultSortIdx={3}
          render={(r: SegRow, i) => {
            const sm = segMetrics(r);
            const seg = r.segments || {};
            const gv = r.geographicView || r.geographic_view || {};
            const countryId = String(gv.countryCriterionId || gv.country_criterion_id || '');
            const loc = geoName(seg.geoTargetCity || seg.geo_target_city)
              || geoName(seg.geoTargetRegion || seg.geo_target_region)
              || (countryId ? (geoNameMap[countryId] || countryId) : '')
              || '—';
            return (
              <tr key={i}>
                <td className="name">{loc}</td>
                <td className="status">{lower(gv.locationType || gv.location_type)}</td>
                <td className="status">{(r.campaign || {}).name || ''}</td>
                <td className="num">{fmtCur(sm.cost, currency)}</td>
                <td className="num">{fmtInt(sm.clicks)}</td>
                <td className="num">{sm.conversions.toFixed(2)}</td>
              </tr>
            );
          }}
        />
      )}

      {/* ── Hour-of-week heatmap ─────────────────────────────────── */}
      {tab === 'hours' && (
        hoursPull.loading ? <div className="gads-empty">loading…</div>
          : hoursPull.error ? <div className="gads-empty">error: {hoursPull.error}</div>
          : hoursPull.rows.length === 0 ? <div className="gads-empty">No hourly data in {rangeLabel}.</div>
          : <HoursHeatmap rows={hoursPull.rows} currency={currency} />
      )}

      {/* ── Demographics (age + gender) ──────────────────────────── */}
      {tab === 'demographics' && (
        <div>
          <h4 className="gads-deep__subhead">age range</h4>
          <DeepTable
            empty={agePull.loading ? 'loading…' : agePull.error ? `error: ${agePull.error}` : `No age data in ${rangeLabel}.`}
            rows={agePull.rows}
            cols={[
              { label: 'age range', sort: (r: SegRow) => lower((((r.adGroupCriterion || r.ad_group_criterion || {}) as any).ageRange || ((r.ad_group_criterion || {}) as any).age_range || {}).type) },
              { label: 'cost', sort: (r: SegRow) => segMetrics(r).cost },
              { label: 'clicks', sort: (r: SegRow) => segMetrics(r).clicks },
              { label: 'conv.', sort: (r: SegRow) => segMetrics(r).conversions },
            ]}
            numCols={[1, 2, 3]}
            defaultSortIdx={1}
            render={(r: SegRow, i) => {
              const sm = segMetrics(r);
              const t = ((r.adGroupCriterion || r.ad_group_criterion || {}).ageRange || (r.ad_group_criterion || {}).age_range || {}).type;
              return (
                <tr key={i}>
                  <td className="name">{lower(t)}</td>
                  <td className="num">{fmtCur(sm.cost, currency)}</td>
                  <td className="num">{fmtInt(sm.clicks)}</td>
                  <td className="num">{sm.conversions.toFixed(2)}</td>
                </tr>
              );
            }}
          />
          <h4 className="gads-deep__subhead">gender</h4>
          <DeepTable
            empty={genderPull.loading ? 'loading…' : genderPull.error ? `error: ${genderPull.error}` : `No gender data in ${rangeLabel}.`}
            rows={genderPull.rows}
            cols={[
              { label: 'gender', sort: (r: SegRow) => lower((((r.adGroupCriterion || r.ad_group_criterion || {}) as any).gender || {}).type) },
              { label: 'cost', sort: (r: SegRow) => segMetrics(r).cost },
              { label: 'clicks', sort: (r: SegRow) => segMetrics(r).clicks },
              { label: 'conv.', sort: (r: SegRow) => segMetrics(r).conversions },
            ]}
            numCols={[1, 2, 3]}
            defaultSortIdx={1}
            render={(r: SegRow, i) => {
              const sm = segMetrics(r);
              const t = ((r.adGroupCriterion || r.ad_group_criterion || {}).gender || {}).type;
              return (
                <tr key={i}>
                  <td className="name">{lower(t)}</td>
                  <td className="num">{fmtCur(sm.cost, currency)}</td>
                  <td className="num">{fmtInt(sm.clicks)}</td>
                  <td className="num">{sm.conversions.toFixed(2)}</td>
                </tr>
              );
            }}
          />
        </div>
      )}

      {/* ── Confirm modal for user-initiated direct changes ──────────── */}
      {rsaOpen && (
        <div className="gads-neg-modal__overlay" onClick={() => { if (!rsaApplying) setRsaOpen(false); }}>
          <div className="gads-rsa" onClick={(e) => e.stopPropagation()}>
            <div className="gads-neg-modal__title">Create responsive search ad → {rsaTarget.name}</div>

            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Final URL</label>
              <input type="url" value={rsaUrl} onChange={(e) => setRsaUrl(e.target.value)} placeholder="https://yourclient.com.au/service" className="gads-neg-modal__input gads-rsa__wide" autoFocus />
              <div className="gads-rsa__pathrow">
                <span className="gads-neg-modal__mt">display path:&nbsp; {rsaUrl.replace(/^https?:\/\//, '').split('/')[0] || 'domain.com'}/</span>
                <input type="text" maxLength={15} value={rsaPath1} onChange={(e) => setRsaPath1(e.target.value)} placeholder="path1" className="gads-neg-modal__input gads-rsa__path" />
                <span className="gads-neg-modal__mt">/</span>
                <input type="text" maxLength={15} value={rsaPath2} onChange={(e) => setRsaPath2(e.target.value)} placeholder="path2" className="gads-neg-modal__input gads-rsa__path" disabled={!rsaPath1.trim()} />
              </div>
            </div>

            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Headlines <span className="gads-neg-modal__mt">(3–15, ≤30 chars; pin to lock a position)</span></label>
              {rsaHeads.map((h, i) => (
                <div key={i} className="gads-rsa__assetrow">
                  <input type="text" maxLength={30} value={h.text} onChange={(e) => setRsaHeads((prev) => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder={`headline ${i + 1}`} className="gads-neg-modal__input gads-rsa__wide" />
                  <span className={`gads-rsa__count${h.text.length > 30 ? ' gads-rsa__count--over' : ''}`}>{h.text.length}/30</span>
                  <select value={h.pin} onChange={(e) => setRsaHeads((prev) => prev.map((x, j) => j === i ? { ...x, pin: e.target.value } : x))} className="gads-neg-modal__input gads-rsa__pin" title="Pin this headline to a fixed position">
                    <option value="">unpinned</option>
                    <option value="HEADLINE_1">pin H1</option>
                    <option value="HEADLINE_2">pin H2</option>
                    <option value="HEADLINE_3">pin H3</option>
                  </select>
                  {rsaHeads.length > 3 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => setRsaHeads((prev) => prev.filter((_, j) => j !== i))} title="Remove">×</button>}
                </div>
              ))}
              {rsaHeads.length < 15 && <button type="button" className="gads-deep__act-btn" onClick={() => setRsaHeads((prev) => [...prev, { text: '', pin: '' }])}>+ headline</button>}
            </div>

            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Descriptions <span className="gads-neg-modal__mt">(2–4, ≤90 chars)</span></label>
              {rsaDescs.map((d, i) => (
                <div key={i} className="gads-rsa__assetrow">
                  <input type="text" maxLength={90} value={d.text} onChange={(e) => setRsaDescs((prev) => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder={`description ${i + 1}`} className="gads-neg-modal__input gads-rsa__wide" />
                  <span className={`gads-rsa__count${d.text.length > 90 ? ' gads-rsa__count--over' : ''}`}>{d.text.length}/90</span>
                  <select value={d.pin} onChange={(e) => setRsaDescs((prev) => prev.map((x, j) => j === i ? { ...x, pin: e.target.value } : x))} className="gads-neg-modal__input gads-rsa__pin">
                    <option value="">unpinned</option>
                    <option value="DESCRIPTION_1">pin D1</option>
                    <option value="DESCRIPTION_2">pin D2</option>
                  </select>
                  {rsaDescs.length > 2 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => setRsaDescs((prev) => prev.filter((_, j) => j !== i))} title="Remove">×</button>}
                </div>
              ))}
              {rsaDescs.length < 4 && <button type="button" className="gads-deep__act-btn" onClick={() => setRsaDescs((prev) => [...prev, { text: '', pin: '' }])}>+ description</button>}
            </div>

            {(() => {
              const hs = rsaHeads.map((h) => h.text.trim()).filter(Boolean);
              const ds = rsaDescs.map((d) => d.text.trim()).filter(Boolean);
              if (!hs.length) return null;
              const domain = rsaUrl.replace(/^https?:\/\//, '').split('/')[0] || 'domain.com';
              return (
                <div className="gads-rsa__preview">
                  <div className="gads-rsa__preview-head">{hs.slice(0, 3).join(' | ')}</div>
                  <div className="gads-rsa__preview-url">{domain}{rsaPath1.trim() ? `/${rsaPath1.trim()}` : ''}{rsaPath2.trim() ? `/${rsaPath2.trim()}` : ''}</div>
                  <div className="gads-rsa__preview-desc">{ds.slice(0, 2).join(' ')}</div>
                  <div className="gads-neg-modal__mt" style={{ marginTop: 4 }}>preview — Google mixes the assets; pinned ones keep their slot</div>
                </div>
              );
            })()}

            <div className="gads-neg-modal__warn">Created <strong>PAUSED</strong> — it will not serve until you enable it. Google policy-checks the ad at creation (a mismatched domain or policy issue is rejected before anything is created).</div>
            <div className="gads-neg-modal__actions">
              <button type="button" className="gads-neg-modal__cancel" disabled={rsaApplying} onClick={() => setRsaOpen(false)}>Cancel</button>
              <button type="button" className="gads-neg-modal__apply" disabled={rsaApplying} onClick={applyRsa}>{rsaApplying ? 'creating…' : 'Create ad (paused)'}</button>
            </div>
          </div>
        </div>
      )}

      {actionModal && (
        <div className="gads-neg-modal__overlay" onClick={() => !actionApplying && setActionModal(null)}>
          <div className="gads-neg-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="gads-neg-modal__title">{actionModal.title}</div>
            <div className="gads-neg-modal__body">{actionModal.lines}</div>
            {actionModal.input === 'bid' && (
              <div className="gads-neg-modal__inputrow">
                <span>{currency || ''}</span>
                <input type="number" inputMode="decimal" min="0" step="0.01" value={modalBid} onChange={(e) => setModalBid(e.target.value)} className="gads-neg-modal__input" autoFocus />
                <span className="gads-neg-modal__mt">max CPC</span>
              </div>
            )}
            {actionModal.input === 'keyword' && (
              <div className="gads-neg-modal__inputrow">
                <input type="text" value={modalKwText} onChange={(e) => setModalKwText(e.target.value)} placeholder="keyword text" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                <select value={modalKwMatch} onChange={(e) => setModalKwMatch(e.target.value)} className="gads-neg-modal__input">
                  <option value="BROAD">broad</option>
                  <option value="PHRASE">phrase</option>
                  <option value="EXACT">exact</option>
                </select>
              </div>
            )}
            {actionModal.input === 'pct' && (
              <div className="gads-neg-modal__inputrow">
                <input type="number" inputMode="decimal" min="-100" max="900" step="5" value={modalPct} onChange={(e) => setModalPct(e.target.value)} placeholder="0" className="gads-neg-modal__input" autoFocus />
                <span className="gads-neg-modal__mt">% adjustment (+20 = bid up 20%, −15 = down, −100 = exclude)</span>
              </div>
            )}
            {actionModal.input === 'adgroup' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" maxLength={255} value={modalKwText} onChange={(e) => setModalKwText(e.target.value)} placeholder="ad group name" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <span>{currency || ''}</span>
                  <input type="number" inputMode="decimal" min="0" step="0.01" value={modalBid} onChange={(e) => setModalBid(e.target.value)} placeholder="optional" className="gads-neg-modal__input" />
                  <span className="gads-neg-modal__mt">default max CPC (optional)</span>
                </div>
              </>
            )}
            {/* F27: sitelink */}
            {actionModal.input === 'sitelink' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" maxLength={25} value={assetText} onChange={(e) => setAssetText(e.target.value)} placeholder="sitelink text (25 max)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <input type="url" value={assetUrl} onChange={(e) => setAssetUrl(e.target.value)} placeholder="https://…" className="gads-neg-modal__input" style={{ flex: 1 }} />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" maxLength={35} value={assetDesc1} onChange={(e) => setAssetDesc1(e.target.value)} placeholder="description line 1 (optional, 35 max)" className="gads-neg-modal__input" style={{ flex: 1 }} />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" maxLength={35} value={assetDesc2} onChange={(e) => setAssetDesc2(e.target.value)} placeholder="description line 2 (optional, 35 max)" className="gads-neg-modal__input" style={{ flex: 1 }} />
                </div>
              </>
            )}
            {/* F27: callout */}
            {actionModal.input === 'callout' && (
              <div className="gads-neg-modal__inputrow">
                <input type="text" maxLength={25} value={assetText} onChange={(e) => setAssetText(e.target.value)} placeholder="callout text (25 max)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                <select value={assetScope} onChange={(e) => setAssetScope(e.target.value)} className="gads-neg-modal__input">
                  <option value="account">account-wide</option>
                  <option value="campaign" disabled={!campFilter}>this campaign</option>
                </select>
              </div>
            )}
            {/* F47: create shared list */}
            {actionModal.input === 'sharedListName' && (
              <div className="gads-neg-modal__inputrow">
                <input type="text" maxLength={255} value={sharedListName} onChange={(e) => setSharedListName(e.target.value)} placeholder="list name (e.g. Brand exclusions)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
              </div>
            )}
            {/* F47: add negative to shared list */}
            {actionModal.input === 'sharedListNegative' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" inputMode="numeric" value={sharedSetIdInput} onChange={(e) => setSharedSetIdInput(e.target.value)} placeholder="shared-set id (numeric)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" value={sharedNegText} onChange={(e) => setSharedNegText(e.target.value)} placeholder="negative keyword text" className="gads-neg-modal__input" style={{ flex: 1 }} />
                  <select value={sharedNegMatch} onChange={(e) => setSharedNegMatch(e.target.value)} className="gads-neg-modal__input">
                    <option value="BROAD">broad</option>
                    <option value="PHRASE">phrase</option>
                    <option value="EXACT">exact</option>
                  </select>
                </div>
              </>
            )}
            {/* F47: attach shared list to campaign */}
            {actionModal.input === 'attachCampaign' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <input type="text" inputMode="numeric" value={sharedSetIdInput} onChange={(e) => setSharedSetIdInput(e.target.value)} placeholder="shared-set id (numeric)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <select value={attachCampaignId} onChange={(e) => setAttachCampaignId(e.target.value)} className="gads-neg-modal__input" style={{ flex: 1 }}>
                    <option value="">pick a campaign…</option>
                    {campaigns.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.id}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {/* F26: bidding strategy */}
            {actionModal.input === 'biddingStrategy' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <select value={editBiddingStrategy} onChange={(e) => setEditBiddingStrategy(e.target.value)} className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus>
                    <option value="MANUAL_CPC">Manual CPC</option>
                    <option value="MAXIMIZE_CONVERSIONS">Maximize Conversions</option>
                    <option value="MAXIMIZE_CONVERSION_VALUE">Maximize Conversion Value</option>
                  </select>
                </div>
                {editBiddingStrategy === 'MAXIMIZE_CONVERSIONS' && (
                  <div className="gads-neg-modal__inputrow">
                    <span>{currency || ''}</span>
                    <input type="number" inputMode="decimal" min="0" step="0.5" value={editTargetCpa} onChange={(e) => setEditTargetCpa(e.target.value)} placeholder="target CPA (optional)" className="gads-neg-modal__input" />
                  </div>
                )}
                {editBiddingStrategy === 'MAXIMIZE_CONVERSION_VALUE' && (
                  <div className="gads-neg-modal__inputrow">
                    <input type="number" inputMode="decimal" min="0" step="0.5" value={editTargetRoas} onChange={(e) => setEditTargetRoas(e.target.value)} placeholder="target ROAS, e.g. 4 (optional)" className="gads-neg-modal__input" style={{ flex: 1 }} />
                  </div>
                )}
              </>
            )}
            {/* F26: networks */}
            {actionModal.input === 'networks' && (
              <div className="gads-neg-modal__inputrow" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={editSearchPartners} onChange={(e) => setEditSearchPartners(e.target.checked)} /> include Google search partners
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={editContentNetwork} onChange={(e) => setEditContentNetwork(e.target.checked)} /> include Display / Content network
                </label>
              </div>
            )}
            {/* F26: device bid */}
            {actionModal.input === 'deviceBid' && (
              <div className="gads-neg-modal__inputrow">
                <select value={editDeviceType} onChange={(e) => setEditDeviceType(e.target.value)} className="gads-neg-modal__input">
                  <option value="MOBILE">mobile</option>
                  <option value="DESKTOP">desktop</option>
                  <option value="TABLET">tablet</option>
                </select>
                <input type="number" inputMode="decimal" min="-100" max="900" step="5" value={editDeviceModifier} onChange={(e) => setEditDeviceModifier(e.target.value)} placeholder="0" className="gads-neg-modal__input" autoFocus />
                <span className="gads-neg-modal__mt">% adjustment (+20 = up, −100 = exclude)</span>
              </div>
            )}
            {/* F26: ad schedule */}
            {actionModal.input === 'adSchedule' && (
              <>
                <div className="gads-neg-modal__inputrow">
                  <select value={editScheduleDay} onChange={(e) => setEditScheduleDay(e.target.value)} className="gads-neg-modal__input">
                    {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((d) => <option key={d} value={d}>{d.toLowerCase()}</option>)}
                  </select>
                  <input type="number" inputMode="numeric" min="0" max="23" value={editScheduleStart} onChange={(e) => setEditScheduleStart(e.target.value)} placeholder="8" className="gads-neg-modal__input" style={{ width: 64 }} />
                  <span className="gads-neg-modal__mt">to</span>
                  <input type="number" inputMode="numeric" min="1" max="24" value={editScheduleEnd} onChange={(e) => setEditScheduleEnd(e.target.value)} placeholder="18" className="gads-neg-modal__input" style={{ width: 64 }} />
                </div>
                <div className="gads-neg-modal__inputrow">
                  <input type="number" inputMode="decimal" min="-90" max="900" step="5" value={editScheduleModifier} onChange={(e) => setEditScheduleModifier(e.target.value)} placeholder="bid modifier % (optional)" className="gads-neg-modal__input" style={{ flex: 1 }} />
                </div>
              </>
            )}
            {/* F26: location */}
            {actionModal.input === 'location' && (
              <div className="gads-neg-modal__inputrow">
                <input type="text" inputMode="numeric" value={editGeoId} onChange={(e) => setEditGeoId(e.target.value)} placeholder="geoTargetConstant id (e.g. 2036)" className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus />
                <span className="gads-neg-modal__mt">numeric id required</span>
              </div>
            )}
            {/* F26: language */}
            {actionModal.input === 'language' && (
              <div className="gads-neg-modal__inputrow">
                <select value={editLanguageId} onChange={(e) => setEditLanguageId(e.target.value)} className="gads-neg-modal__input" style={{ flex: 1 }} autoFocus>
                  {WIZARD_LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
            {actionModal.warn && <div className="gads-neg-modal__warn">{actionModal.warn}</div>}
            <div className="gads-neg-modal__actions">
              <button type="button" className="gads-neg-modal__cancel" disabled={actionApplying} onClick={() => setActionModal(null)}>Cancel</button>
              <button type="button" className={actionModal.danger ? 'gads-neg-modal__danger' : 'gads-neg-modal__apply'} disabled={actionApplying} onClick={confirmAction}>
                {actionModal.draftOnly ? (actionApplying ? 'Drafting…' : 'Draft for review') : (actionApplying ? 'Applying…' : 'Apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ── Hour-of-week heatmap (7 days x 24 hours, shaded by cost) ─────────────
const DOW_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const HoursHeatmap: React.FC<{ rows: SegRow[]; currency?: string }> = ({ rows, currency }) => {
  // Tap-to-reveal for touch — title tooltips don't exist on phones, so a tapped
  // cell's value is echoed into the note line below the grid.
  const [picked, setPicked] = useState<{ day: string; hour: number; cost: number } | null>(null);
  // grid[day][hour] = cost
  const grid: Record<string, number[]> = {};
  let max = 0;
  for (const d of DOW_ORDER) grid[d] = new Array(24).fill(0);
  for (const r of rows) {
    const seg = r.segments || {};
    const day = String(seg.dayOfWeek || seg.day_of_week || '').toUpperCase();
    const hour = n(seg.hour);
    if (!grid[day] || hour < 0 || hour > 23) continue;
    const cost = microsToAmount((r.metrics || {}).costMicros);
    grid[day][hour] += cost;
    if (grid[day][hour] > max) max = grid[day][hour];
  }
  const shade = (v: number) => {
    if (max <= 0 || v <= 0) return 'transparent';
    const a = 0.08 + 0.55 * (v / max);
    return `rgba(78, 192, 124, ${a.toFixed(3)})`;
  };
  return (
    <div className="gads-heatmap-wrap">
      <table className="gads-heatmap">
        <thead>
          <tr>
            <th></th>
            {Array.from({ length: 24 }, (_, h) => <th key={h} className="num">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {DOW_ORDER.map((day) => (
            <tr key={day}>
              <td className="gads-heatmap__day">{day.slice(0, 3).toLowerCase()}</td>
              {grid[day].map((v, h) => (
                <td
                  key={h}
                  className={`gads-heatmap__cell${picked && picked.day === day && picked.hour === h ? ' gads-heatmap__cell--picked' : ''}`}
                  style={{ background: shade(v) }}
                  title={`${day.toLowerCase()} ${h}:00 — ${fmtCur(v, currency)}`}
                  onClick={() => setPicked({ day, hour: h, cost: v })}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gads-heatmap__note">
        {picked
          ? <><strong>{picked.day.slice(0, 3).toLowerCase()} {picked.hour}:00 — {fmtCur(picked.cost, currency)}</strong> · </>
          : null}
        cost by day-of-week × hour (account timezone) · darker = more spend{picked ? '' : ' · tap a cell for its value'}
      </div>
    </div>
  );
};

// ── AI Account Audit panel ──────────────────────────────────────────────
// Renders the structured audit returned by auditGoogleAdsAccount: a health
// score, narrative summary, ranked priorities, quick wins, and caveats.
const SEV_DOT: Record<string, string> = { high: '🔴', medium: '🟡', low: '⚪' };

function scoreClass(score: number): string {
  if (score >= 75) return 'gads-audit__score--good';
  if (score >= 50) return 'gads-audit__score--fair';
  return 'gads-audit__score--poor';
}

const AuditPanel: React.FC<{
  loading: boolean;
  error: string;
  data: AuditResponse | null;
  onClose: () => void;
}> = ({ loading, error, data, onClose }) => {
  const audit = data?.audit;
  return (
    <section className="gads-audit">
      <div className="gads-audit__head">
        <span className="gads-audit__badge">✦ AI Account Audit</span>
        {data?.model?.used && (
          <span className="gads-audit__model">{data.model.provider} · {data.model.used}</span>
        )}
        {!loading && (
          <button type="button" className="gads-audit__close" onClick={onClose} title="Dismiss">✕</button>
        )}
      </div>

      {loading && (
        <div className="gads-audit__loading">
          Analysing the account’s synced data and writing the audit… this takes a few seconds.
        </div>
      )}

      {!loading && error && (
        <div className="gads-audit__error">⚠ {error}</div>
      )}

      {!loading && !error && audit && (
        <div className="gads-audit__body">
          <div className="gads-audit__top">
            <div className={`gads-audit__score ${scoreClass(audit.healthScore)}`}>
              <div className="gads-audit__score-num">{audit.healthScore}</div>
              <div className="gads-audit__score-label">{audit.healthLabel}</div>
            </div>
            <div className="gads-audit__headline">{audit.headline}</div>
          </div>

          {audit.summary && (
            <div className="gads-audit__summary">
              {audit.summary.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
            </div>
          )}

          {audit.priorities.length > 0 && (
            <div className="gads-audit__section">
              <h4>Priorities</h4>
              <ol className="gads-audit__priorities">
                {audit.priorities.map((p, i) => (
                  <li key={i} className={`gads-audit__priority gads-audit__priority--${p.severity}`}>
                    <div className="gads-audit__priority-title">
                      <span className="gads-audit__sev">{SEV_DOT[p.severity] || '⚪'}</span> {p.title}
                    </div>
                    {p.rationale && <div className="gads-audit__priority-why">{p.rationale}</div>}
                    {p.action && <div className="gads-audit__priority-action">→ {p.action}</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {audit.quickWins.length > 0 && (
            <div className="gads-audit__section">
              <h4>Quick wins</h4>
              <ul className="gads-audit__list">
                {audit.quickWins.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {audit.watchOuts.length > 0 && (
            <div className="gads-audit__section gads-audit__section--muted">
              <h4>Watch-outs</h4>
              <ul className="gads-audit__list">
                {audit.watchOuts.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="gads-audit__foot">
            Generated from synced data (as of the last sync). Read-only — nothing was changed in Google Ads.
          </div>
        </div>
      )}
    </section>
  );
};

// ── Generic table shell ─────────────────────────────────────────────────
// numCols = indices of right-aligned numeric columns (so headers line up
// with the right-aligned data cells).
/** Column spec. A plain string is an unsortable label (back-compat); give
 *  an object with `sort` to make the header clickable. Accessors may
 *  return string (locale text sort) or number; NaN/non-finite numbers sink
 *  to the bottom in BOTH directions (the "no data" sentinel — a keyword
 *  with no QS should never win a QS sort). */
type ColSpec<T> = string | { label: string; sort?: (row: T) => number | string };

function DeepTable<T>({ rows, cols, numCols, render, empty, defaultSortIdx }: {
  rows: T[];
  cols: Array<ColSpec<T>>;
  numCols: number[];
  render: (row: T, i: number) => React.ReactNode;
  empty: string;
  /** Column index initially sorted (desc for num columns, asc for text).
   *  Omit to keep the caller's input order until a header is clicked. */
  defaultSortIdx?: number;
}) {
  const numSet = useMemo(() => new Set(numCols), [numCols]);

  // A newly-clicked column's natural first direction is inferred from the
  // DATA the accessor returns (number → desc "biggest first"; text → asc
  // A–Z), NOT from numCols (which is only about cell text-alignment). This
  // makes e.g. the recommendations 'est. impact' column — numeric but not
  // in numCols — sort high-to-low on first click like every other metric.
  const naturalDir = useCallback((idx: number): 'asc' | 'desc' => {
    const spec = cols[idx];
    if (typeof spec === 'string' || !spec.sort) return 'desc';
    const sample = rows.length ? spec.sort(rows[0]) : undefined;
    return typeof sample === 'number' ? 'desc' : 'asc';
  }, [cols, rows]);

  const [sortIdx, setSortIdx] = useState<number>(defaultSortIdx ?? -1);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Set the initial direction once rows exist (accessor sampling needs a row).
  const initDone = useRef(false);
  useEffect(() => {
    if (initDone.current || defaultSortIdx === undefined || rows.length === 0) return;
    setSortDir(naturalDir(defaultSortIdx));
    initDone.current = true;
  }, [defaultSortIdx, rows.length, naturalDir]);

  const onSort = (idx: number) => {
    const spec = cols[idx];
    if (typeof spec === 'string' || !spec.sort) return;
    if (idx === sortIdx) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortIdx(idx);
      setSortDir(naturalDir(idx));
    }
  };

  const sorted = useMemo(() => {
    if (sortIdx < 0) return rows;
    const spec = cols[sortIdx];
    if (typeof spec === 'string' || !spec.sort) return rows;
    const acc = spec.sort;
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = acc(a); const vb = acc(b);
      if (typeof va === 'string' || typeof vb === 'string') {
        return String(va).localeCompare(String(vb)) * mul;
      }
      const na = isFinite(va as number) ? (va as number) : (sortDir === 'asc' ? Infinity : -Infinity);
      const nb = isFinite(vb as number) ? (vb as number) : (sortDir === 'asc' ? Infinity : -Infinity);
      return (na - nb) * mul;
    });
    // cols is recreated each render (inline literals), so this memo re-sorts
    // per render — deliberate: the accessors close over late-loading state
    // (campaign-name lookups, session-negated sets) and a cached sort would
    // go stale. Sorting ≤2k rows is sub-millisecond; correctness wins.
  }, [rows, cols, sortIdx, sortDir]);

  if (rows.length === 0) return <div className="gads-empty">{empty}</div>;
  return (
    <div className="gads-table-wrap">
      <table className="gads-table">
        <thead>
          <tr>
            {cols.map((c, i) => {
              const spec = typeof c === 'string' ? { label: c, sort: undefined } : c;
              const sortable = !!spec.sort;
              const active = sortable && sortIdx === i;
              const cls = [numSet.has(i) ? 'num' : '', sortable ? 'sortable' : '', active ? 'sorted' : ''].filter(Boolean).join(' ');
              return (
                <th
                  key={i}
                  className={cls}
                  {...(sortable ? {
                    onClick: () => onSort(i),
                    role: 'button',
                    tabIndex: 0,
                    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(i); } },
                    title: `Sort by ${spec.label}`,
                  } : {})}
                >
                  {spec.label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{sorted.map((r, i) => render(r, i))}</tbody>
      </table>
    </div>
  );
}

export default AccountDeepSections;
